import { useCallback, useEffect, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowsClockwise, ArrowSquareOut, CheckCircle, Warning, Article, Eye, Image as ImageIcon, Link as LinkIcon, Lightning, MagicWand, Storefront, UsersThree, ListChecks, CaretUp, CaretDown, Sparkle, X, Pulse, Stethoscope, ThumbsUp, ThumbsDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  ArticleAiExtrasFields,
  buildAiExtrasPayload,
} from "./components/ArticleAiExtrasFields";

const fmtUsd = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16); }
};

const fmtDateShort = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "2-digit" });
  } catch { return iso.slice(0, 10); }
};

/**
 * Auto-Article admin — 4 tabs:
 *   Overview     — status, action buttons, recent articles (existing)
 *   Brands       — sortable table, auto_publish toggle, "Generate now"
 *   Publishers   — pubs ranked by gross sales, articles attributed
 *   Articles     — filterable feed
 */
export default function AdminAutoArticle() {
  const [tab, setTab] = useState("overview");

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="admin-auto-article">
      <Header />
      <TabNav tab={tab} setTab={setTab} />
      {tab === "overview" && <OverviewTab />}
      {tab === "brands" && <BrandsTab />}
      {tab === "publishers" && <PublishersTab />}
      {tab === "articles" && <ArticlesTab />}
      {tab === "diagnostics" && <DiagnosticsTab />}
    </div>
  );
}

function Header() {
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
        <Article weight="duotone" size={28} className="text-indigo-600" />
        Auto-Article Pipeline
      </h1>
      <p className="text-sm text-slate-500 mt-1">
        Conversions ≥ $10 USD auto-publish to wildbloomfamilies.com hourly. Tier-based article quota per (brand × publisher) — entry tier $10–$299 → 1 article. 75-day refresh runs nightly.
      </p>
    </div>
  );
}

function TabNav({ tab, setTab }) {
  const tabs = [
    { id: "overview", label: "Overview", icon: Lightning },
    { id: "brands", label: "Brands", icon: Storefront },
    { id: "publishers", label: "Publishers", icon: UsersThree },
    { id: "articles", label: "Articles", icon: ListChecks },
    { id: "diagnostics", label: "Diagnostics", icon: Stethoscope },
  ];
  return (
    <div className="flex gap-1 border-b border-slate-200">
      {tabs.map((t) => {
        const Icon = t.icon;
        const active = tab === t.id;
        return (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              active
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
            }`}
            data-testid={`aa-tab-${t.id}`}
          >
            <Icon size={16} weight={active ? "fill" : "regular"} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Overview tab (existing layout, condensed) ─────────────────────

function OverviewTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  // Detail drawer + queue stats — these used to live only in
  // ArticlesTab, which is why clicking "Details" / "View error" on
  // the Overview tab silently did nothing (the row called
  // onOpenDetail but no handler / drawer were wired here).
  const [detail, setDetail] = useState(null);
  const [queueStats, setQueueStats] = useState({ pending: 0, processing: 0, completed: 0, failed: 0 });
  const [bulkRetrying, setBulkRetrying] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [r, q] = await Promise.all([
        API.get("/admin/auto-article/status"),
        API.get("/admin/auto-article/retry-queue/stats").catch(() => ({ data: queueStats })),
      ]);
      setStatus(r.data);
      if (q?.data) setQueueStats(q.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load status");
    } finally { setLoading(false); }
  }, []);

  // Sequential bulk-retry handler — fires the backend job that runs
  // one-at-a-time so we don't recreate the WP 503 issue we hit when
  // 4 parallel publishes hammered SiteGround.
  const reprocessStuck = async () => {
    if (!window.confirm("Reprocess ALL articles stuck >5min in 'In progress'? They will be queued and processed sequentially.")) return;
    setBulkRetrying(true);
    try {
      const r = await API.post("/admin/auto-article/bulk-retry", { reprocess_stuck: true, stuck_threshold_minutes: 5 });
      toast.success(`Queued ${r.data.accepted} stuck articles (skipped ${r.data.skipped} already queued)`);
      fetchStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk retry failed"); }
    finally { setBulkRetrying(false); }
  };

  useEffect(() => {
    fetchStatus();
    // Soft background refresh only — no loading flash. Manual Refresh
    // button still uses the full fetchStatus path.
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      API.get("/admin/auto-article/status")
        .then((r) => setStatus(r.data))
        .catch(() => {});
      API.get("/admin/auto-article/retry-queue/stats")
        .then((q) => { if (q?.data) setQueueStats(q.data); })
        .catch(() => {});
    }, 60_000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const enabled = status?.enabled === "true" || status?.enabled === true;
  const articles = status?.recent_articles || [];

  const runDryRun = async () => {
    if (!window.confirm("Run one full polling pass NOW? Will publish real articles for any qualifying conversions.")) return;
    setDryRunLoading(true);
    try {
      const r = await API.post("/admin/auto-article/dry-run");
      const s = r.data;
      toast.success(`checked=${s.checked} published=${s.published} skipped=${s.skipped} errors=${s.errors}`);
      fetchStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setDryRunLoading(false); }
  };

  const runRefresh = async () => {
    if (!window.confirm("Run the 75-day refresh pass NOW? Re-scrapes aging articles + PATCHes existing WP posts.")) return;
    setRefreshLoading(true);
    try {
      const r = await API.post("/admin/auto-article/refresh-now");
      const s = r.data;
      toast.success(`checked=${s.checked} refreshed=${s.refreshed} errors=${s.errors}`);
      fetchStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRefreshLoading(false); }
  };

  const fetchPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await API.get("/admin/auto-article/qualifying", { params: { hours: 24 } });
      setPreview(r.data);
      toast.success(`${r.data?.count ?? 0} qualifying conversions`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setPreviewLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={fetchStatus} disabled={loading} data-testid="aa-refresh-btn">
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={fetchPreview} disabled={previewLoading} data-testid="aa-preview-btn">
          <Eye size={14} className={previewLoading ? "animate-spin" : ""} />Preview qualifying
        </Button>
        <Button size="sm" onClick={runDryRun} disabled={dryRunLoading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="aa-dry-run-btn">
          <MagicWand size={14} className={dryRunLoading ? "animate-spin" : ""} />Run pass now
        </Button>
        <Button size="sm" variant="outline" onClick={runRefresh} disabled={refreshLoading} className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="aa-refresh-job-btn">
          <ArrowsClockwise size={14} className={refreshLoading ? "animate-spin" : ""} />Run 75d refresh
        </Button>
        <Button size="sm" variant="outline" onClick={reprocessStuck} disabled={bulkRetrying} className="border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="aa-reprocess-stuck-btn" title="Re-queue all 'In progress' articles that have been stuck for >5 minutes (server restart / crash recovery)">
          <ArrowsClockwise size={14} className={bulkRetrying ? "animate-spin" : ""} />Reprocess stuck
        </Button>
      </div>

      {/* Queue stats tile — only shown when there is queue activity,
          so a healthy idle pipeline doesn't get visually polluted. */}
      {(queueStats.pending + queueStats.processing + queueStats.completed + queueStats.failed) > 0 && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4" data-testid="aa-queue-stats-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
              <ArrowsClockwise size={14} className={queueStats.processing > 0 ? "animate-spin text-indigo-600" : "text-slate-400"} />
              Retry queue
              <span className="text-xs text-slate-500 font-normal">sequential (one at a time)</span>
            </h2>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <QueueStatTile label="Pending"    count={queueStats.pending}    tone="warn" testId="aa-queue-pending" />
            <QueueStatTile label="Processing" count={queueStats.processing} tone="info" testId="aa-queue-processing" pulse={queueStats.processing > 0} />
            <QueueStatTile label="Completed"  count={queueStats.completed}  tone="ok"   testId="aa-queue-completed" sub="last 24h" />
            <QueueStatTile label="Failed"     count={queueStats.failed}     tone="fail" testId="aa-queue-failed"    sub="last 24h" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatusTile label="Pipeline" value={enabled ? "ENABLED" : "DISABLED"} tone={enabled ? "ok" : "warn"} icon={enabled ? <CheckCircle size={20} weight="fill" /> : <Warning size={20} weight="fill" />} sub={enabled ? "Polling every 1 hour" : "Set AUTO_ARTICLE_PIPELINE_ENABLED=true"} data-testid="aa-tile-enabled" />
        <StatusTile label="Sale threshold" value={fmtUsd(status?.sale_threshold_usd ?? 10)} tone="neutral" icon={<Lightning size={20} weight="duotone" />} sub="Against amount_usd (FX-fair)" data-testid="aa-tile-threshold" />
        <StatusTile label="Default category" value={`id=${status?.default_category_id ?? "—"}`} tone="neutral" icon={<Article size={20} weight="duotone" />} sub="When writer returns no pick" data-testid="aa-tile-default-cat" />
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <CredChip ok={status?.openai_key_set} label="OPENAI_API_KEY" />
        <CredChip ok={status?.anthropic_key_set} label="ANTHROPIC_API_KEY" />
        <CredChip ok={status?.hf_key_set} label="HF_API_KEY" />
        <CredChip ok={status?.validator_enabled} label="VALIDATOR" />
        <CredChip ok={status?.wp_username_set} label="WP_USERNAME" />
        <CredChip ok={status?.wp_app_password_set} label="WP_APP_PASSWORD" />
        {status?.writer_model && (
          <span className="inline-flex items-center px-2 py-1 rounded-md border border-slate-200 bg-white text-slate-600" data-testid="aa-writer-model">
            writer: {status.writer_model}
          </span>
        )}
        {status?.human_approval_required && (
          <span className="inline-flex items-center px-2 py-1 rounded-md border border-amber-200 bg-amber-50 text-amber-800" data-testid="aa-human-approval">
            Human approval required
          </span>
        )}
        {status?.wp_url && (
          <a href={status.wp_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50" data-testid="aa-wp-url-link">
            <LinkIcon size={12} />{status.wp_url.replace(/^https?:\/\//, "")}
          </a>
        )}
      </div>

      {preview && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4" data-testid="aa-preview-panel">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-slate-900 text-sm">
              Next-poll preview: {preview.count} qualifying
              <span className="ml-2 text-xs text-slate-500 font-normal">({fmtUsd(preview.threshold_usd)} threshold, {preview.lookback_hours}h)</span>
            </h2>
            <Button variant="ghost" size="sm" onClick={() => setPreview(null)}><X size={14} /></Button>
          </div>
          {(preview.rows || []).length === 0 ? (
            <p className="text-xs text-slate-500 italic">Nothing to publish on the next pass.</p>
          ) : (
            <ul className="space-y-1 text-xs">
              {preview.rows.slice(0, 10).map((r) => (
                <li key={r.conversion_id} className="flex items-center justify-between gap-3 py-1">
                  <span className="font-mono text-slate-600 truncate">{r.conversion_id}</span>
                  <span className="font-medium text-slate-900 truncate flex-1">{r.brand_name}</span>
                  <span className="text-slate-600 truncate">{r.publisher_email}</span>
                  <span className="font-mono text-emerald-700 tabular-nums whitespace-nowrap">{fmtUsd(r.gross_sale)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ArticlesTable rows={articles} title={`Recent articles (${articles.length})`} subtitle={<span>Last 50 rows from <code className="text-slate-700">article_log</code></span>} onOpenDetail={setDetail} onRetry={fetchStatus} />

      <ArticleDetailDrawer
        conversionId={detail}
        onClose={() => setDetail(null)}
        onRetried={fetchStatus}
      />
    </div>
  );
}

function QueueStatTile({ label, count, tone, sub, pulse, testId }) {
  const toneCls = {
    ok:   "border-emerald-200 bg-white text-emerald-800",
    fail: "border-rose-200 bg-white text-rose-800",
    info: "border-indigo-200 bg-white text-indigo-800",
    warn: "border-amber-200 bg-white text-amber-800",
  }[tone] || "border-slate-200 bg-white text-slate-800";
  return (
    <div className={`rounded-md border px-3 py-2 ${toneCls} ${pulse ? "animate-pulse" : ""}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="text-2xl font-bold tabular-nums leading-tight">{Number(count || 0).toLocaleString()}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Brands tab ────────────────────────────────────────────────────

function BrandsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ col: "gross_sales_usd_90d", dir: "desc" });
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [networkFilter, setNetworkFilter] = useState("");
  const [networks, setNetworks] = useState([]);
  const [page, setPage] = useState(1);
  const [detailBrand, setDetailBrand] = useState(null);
  const [articleDetail, setArticleDetail] = useState(null);
  const [genBrand, setGenBrand] = useState(null);
  const [genUrls, setGenUrls] = useState([""]);
  const [genInstructions, setGenInstructions] = useState("");
  const [genBusy, setGenBusy] = useState(false);

  const BRANDS_PAGE_SIZE = 20;

  // Debounce search so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: BRANDS_PAGE_SIZE,
        sort_by: sort.col,
        sort_dir: sort.dir,
      };
      if (searchDebounced) params.q = searchDebounced;
      if (networkFilter) params.network = networkFilter;
      const r = await API.get("/admin/auto-article/brands", { params });
      setRows(r.data?.rows || []);
      setTotal(r.data?.count || 0);
      setTotalPages(r.data?.total_pages || 1);
      if (Array.isArray(r.data?.networks)) setNetworks(r.data.networks);
      if (r.data?.page && r.data.page !== page) setPage(r.data.page);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  }, [page, sort.col, sort.dir, searchDebounced, networkFilter]);
  useEffect(() => { load(); }, [load]);

  const toggleSort = (col) => {
    setSort((s) => s.col === col
      ? { col, dir: s.dir === "asc" ? "desc" : "asc" }
      : { col, dir: "desc" });
    setPage(1);
  };

  const pageStart = total === 0 ? 0 : (page - 1) * BRANDS_PAGE_SIZE;

  const toggleAutoPublish = async (brand, enabled) => {
    try {
      await API.patch(`/admin/auto-article/brands/${brand.brand_id}/auto-publish`, { auto_publish: enabled });
      toast.success(`${brand.name}: auto_publish ${enabled ? "ON" : "OFF"}`);
      setRows((prev) => prev.map((r) =>
        r.brand_id === brand.brand_id ? { ...r, auto_publish: enabled } : r,
      ));
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };

  const openGenerateDialog = (brand) => {
    setGenBrand(brand);
    setGenUrls([""]);
    setGenInstructions("");
  };

  const generateNow = async () => {
    if (!genBrand) return;
    setGenBusy(true);
    try {
      const payload = buildAiExtrasPayload(genUrls, genInstructions);
      const r = await API.post(
        `/admin/auto-article/brands/${genBrand.brand_id}/generate-now`,
        Object.keys(payload).length ? payload : {},
        { timeout: 60_000 },
      );
      if (r.data.queued) {
        toast.success(`📥 Queued "${genBrand.name}" — variant #${r.data.article_variant}. Watch Articles → In progress / Pending approval.`, { duration: 8000 });
      } else if (r.data.status === "skipped") {
        toast.error(`Skipped: ${r.data.error_reason || "tier quota met"}`, { duration: 8000 });
      } else {
        toast.success(r.data.message || "Queued");
      }
      setGenBrand(null);
      load();
    } catch (e) {
      const data = e?.response?.data;
      const detail = typeof data?.detail === "string"
        ? data.detail
        : (Array.isArray(data?.detail) ? data.detail.map((d) => d.msg || d).join("; ") : null);
      toast.error(
        detail
          || data?.error_reason
          || (e?.code === "ECONNABORTED"
            ? "Generate timed out before the server answered — refresh Articles; it may still have queued."
            : "Generate failed — check Articles tab for details."),
        { duration: 10000 },
      );
    } finally {
      setGenBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search brand name or ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm h-9"
            data-testid="aa-brands-search"
          />
          <select
            value={networkFilter}
            onChange={(e) => { setNetworkFilter(e.target.value); setPage(1); }}
            className="h-9 px-2 border border-slate-200 rounded-md text-sm bg-white min-w-[140px]"
            data-testid="aa-brands-network-filter"
          >
            <option value="">All networks</option>
            {networks.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">
            {total === 0
              ? "0 brands"
              : `${pageStart + 1}–${Math.min(pageStart + BRANDS_PAGE_SIZE, total)} of ${total}`}
          </span>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="aa-brands-refresh">
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
              <tr>
                <SortableTh label="Brand" col="name" sort={sort} onSort={toggleSort} />
                <SortableTh label="Category" col="category" sort={sort} onSort={toggleSort} />
                <SortableTh label="Country" col="country" sort={sort} onSort={toggleSort} />
                <SortableTh label="Pubs" col="num_publishers" sort={sort} onSort={toggleSort} align="right" />
                <SortableTh label="Articles" col="num_articles" sort={sort} onSort={toggleSort} align="right" />
                <SortableTh label="Last article" col="last_article_published" sort={sort} onSort={toggleSort} />
                <SortableTh label="Sales 90d" col="gross_sales_usd_90d" sort={sort} onSort={toggleSort} align="right" />
                <th className="px-3 py-2 text-center">Auto-publish</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && rows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-slate-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-slate-500">No brands match.</td></tr>
              ) : rows.map((b) => (
                <tr key={b.brand_id} className="hover:bg-slate-50/50" data-testid={`aa-brand-row-${b.brand_id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => setDetailBrand(b)} className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium text-left" data-testid={`aa-brand-detail-${b.brand_id}`}>
                      {b.name || b.brand_id}
                    </button>
                    {b.network && (
                      <span className="ml-2 text-[10px] text-slate-400 font-mono">{b.network}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{b.category || "—"}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{b.country || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{b.num_publishers}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{b.num_articles}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{fmtDateShort(b.last_article_published)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-medium">{fmtUsd(b.gross_sales_usd_90d)}</td>
                  <td className="px-3 py-2 text-center">
                    <Switch checked={b.auto_publish} onCheckedChange={(v) => toggleAutoPublish(b, v)} data-testid={`aa-toggle-${b.brand_id}`} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Button variant="outline" size="sm" onClick={() => openGenerateDialog(b)} className="h-7 text-xs" data-testid={`aa-gen-${b.brand_id}`}>
                      <Sparkle size={12} />Generate
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > BRANDS_PAGE_SIZE && (
          <div
            className="px-4 py-2.5 border-t border-slate-200 bg-slate-50/40 flex items-center justify-between text-xs text-slate-600"
            data-testid="aa-brands-pagination"
          >
            <span className="font-mono">
              Page {page} / {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="aa-brands-prev"
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="aa-brands-next"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <BrandDetailSheet
        brand={detailBrand}
        onClose={() => setDetailBrand(null)}
        onOpenArticleDetail={(cid) => {
          setDetailBrand(null);
          setArticleDetail(cid);
        }}
      />
      <ArticleDetailDrawer
        conversionId={articleDetail}
        onClose={() => setArticleDetail(null)}
        onRetried={load}
      />

      <Dialog open={!!genBrand} onOpenChange={(open) => { if (!open) setGenBrand(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="aa-generate-now-dialog">
          <DialogHeader>
            <DialogTitle>Generate article now</DialogTitle>
            <DialogDescription>
              Queue an article for <strong>{genBrand?.name}</strong>. Add
              official shipping / returns / membership URLs for a full guide;
              Approve will update an existing WordPress post for this brand
              when one already exists.
            </DialogDescription>
          </DialogHeader>
          <ArticleAiExtrasFields
            referenceUrls={genUrls}
            setReferenceUrls={setGenUrls}
            instructions={genInstructions}
            setInstructions={setGenInstructions}
            idPrefix="aa-gen-now"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setGenBrand(null)}>Cancel</Button>
            <Button
              type="button"
              onClick={generateNow}
              disabled={genBusy}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="aa-generate-now-submit"
            >
              {genBusy ? "Queuing…" : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SortableTh({ label, col, sort, onSort, align = "left" }) {
  const active = sort.col === col;
  return (
    <th className={`px-3 py-2 text-${align} cursor-pointer select-none hover:bg-slate-100`} onClick={() => onSort(col)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sort.dir === "asc" ? <CaretUp size={10} weight="fill" /> : <CaretDown size={10} weight="fill" />)}
      </span>
    </th>
  );
}

function BrandDetailSheet({ brand, onClose, onOpenArticleDetail }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bans, setBans] = useState([]);

  const reloadDetail = useCallback(() => {
    if (!brand) return;
    setLoading(true);
    Promise.all([
      API.get(`/admin/auto-article/brands/${brand.brand_id}`),
      API.get(`/admin/auto-article/brands/${brand.brand_id}/learned-bans`).catch(() => ({ data: { bans: [] } })),
    ])
      .then(([d, b]) => {
        setDetail(d.data);
        setBans(b.data?.bans || []);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed"))
      .finally(() => setLoading(false));
  }, [brand]);

  useEffect(() => {
    if (!brand) { setDetail(null); setBans([]); return; }
    reloadDetail();
  }, [brand, reloadDetail]);

  if (!brand) return null;

  return (
    <Sheet open={!!brand} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[700px] sm:max-w-[700px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{brand.name}</SheetTitle>
          <SheetDescription>
            <span className="font-mono text-xs">{brand.brand_id}</span>
            {brand.network && <span className="ml-2 text-xs text-slate-500">· {brand.network}</span>}
          </SheetDescription>
        </SheetHeader>

        {loading && <p className="text-sm text-slate-500 mt-6">Loading…</p>}
        {detail && (
          <div className="mt-6 space-y-5">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <KV label="Category" v={detail.brand?.category} />
              <KV label="Country" v={detail.brand?.country || "—"} />
              <KV label="Article count" v={detail.article_count} />
              <KV label="Auto-publish" v={detail.brand?.auto_publish ? "ON" : "OFF"} />
              {detail.brand?.campaign_url && (
                <KV label="Brand URL" v={<a href={detail.brand.campaign_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{detail.brand.campaign_url.replace(/^https?:\/\//, "")}</a>} span={2} />
              )}
              {detail.brand?.description && <KV label="Description" v={detail.brand.description} span={2} />}
            </div>

            {bans.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="aa-brand-learned-bans">
                <h3 className="font-semibold text-sm mb-2 text-amber-900 flex items-center gap-2">
                  <Sparkle size={14} weight="duotone" className="text-amber-600" />
                  Editor learned bans <span className="font-normal text-xs text-amber-700">(last 60d, ≥2× rejected)</span>
                </h3>
                <p className="text-[11px] text-amber-800 mb-2">These phrases will be automatically excluded from the next article&apos;s prompt.</p>
                <div className="flex flex-wrap gap-1.5">
                  {bans.map((b) => (
                    <Badge key={b.phrase} variant="outline" className="border-amber-300 text-amber-800 bg-white text-[10px]" title={`${b.count}× rejected by Mistral`}>
                      {b.phrase} <span className="ml-1 text-amber-500">×{b.count}</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="font-semibold text-sm mb-2">Articles ({detail.articles.length})</h3>
              {detail.articles.length === 0 ? (
                <p className="text-xs text-slate-500 italic">No articles yet for this brand.</p>
              ) : (
                <ArticlesTable
                  rows={detail.articles}
                  compact
                  onOpenDetail={onOpenArticleDetail}
                  onRetry={reloadDetail}
                />
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function KV({ label, v, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-slate-900 mt-0.5">{v ?? "—"}</div>
    </div>
  );
}

// ─── Publishers tab ────────────────────────────────────────────────

function PublishersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    API.get("/admin/auto-article/publishers")
      .then((r) => setRows(r.data?.rows || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <h2 className="font-semibold text-sm text-slate-900">Publishers ranked by 90-day gross sales</h2>
        <span className="text-xs text-slate-500">{rows.length} publishers with auto-article activity</span>
      </div>
      {loading ? (
        <p className="px-4 py-12 text-center text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="px-4 py-12 text-center text-sm text-slate-500">No publishers have triggered articles yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              <th className="px-4 py-2 text-left">Publisher</th>
              <th className="px-4 py-2 text-right">Articles</th>
              <th className="px-4 py-2 text-right">Brands</th>
              <th className="px-4 py-2 text-left">Last article</th>
              <th className="px-4 py-2 text-right">Gross 90d</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((p) => (
              <tr key={p.publisher_id} className="hover:bg-slate-50/50" data-testid={`aa-pub-row-${p.publisher_id}`}>
                <td className="px-4 py-2">
                  <div className="text-slate-900">{p.publisher_email || <span className="font-mono text-xs text-slate-500">{p.publisher_id.slice(0, 14)}…</span>}</div>
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-700">{p.num_articles}</td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-700">{p.num_brands}</td>
                <td className="px-4 py-2 text-xs text-slate-600">{fmtDateShort(p.last_article_published)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-emerald-700 font-medium">{fmtUsd(p.gross_sales_usd_90d)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Articles feed tab ─────────────────────────────────────────────

function ArticlesTab() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [filter, setFilter] = useState({ article_type: "", status: "", editor_quality: "" });
  const [detail, setDetail] = useState(null); // open conversion detail drawer

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const params = { limit: 200 };
    if (filter.article_type) params.article_type = filter.article_type;
    if (filter.status) params.status = filter.status;
    if (filter.editor_quality) params.editor_quality = filter.editor_quality;
    try {
      const [list, sum, up] = await Promise.all([
        API.get("/admin/auto-article/articles", { params }),
        API.get("/admin/auto-article/status-summary"),
        API.get("/admin/auto-article/upcoming", { params: { limit: 2 } }).catch(() => ({ data: null })),
      ]);
      setRows(list.data?.rows || []);
      setSummary(sum.data || null);
      setUpcoming(up.data || null);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  // Manual "Run polling now" — bypasses the hourly scheduler tick.
  // Backend queues check_conversions() in the background and returns
  // instantly. Soft-refresh a few times so the queue updates without
  // flashing the whole table into Loading…
  const runPoll = useCallback(async () => {
    setPolling(true);
    try {
      await API.post("/admin/auto-article/run-poll-now");
      toast.success("Polling tick queued — watch the queue light up below.");
      for (let i = 0; i < 4; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        await load({ silent: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to queue polling");
    } finally {
      setPolling(false);
    }
  }, [load]);

  // Soft-refresh while articles are actively writing — never flash
  // Loading… and NEVER auto-poll for cache_warming (that caused a
  // continuous full reload every 10s while the heavy /upcoming
  // aggregation warms).
  useEffect(() => {
    if (!(summary?.counts?.in_progress > 0)) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load({ silent: true });
    }, 15_000);
    return () => clearInterval(t);
  }, [summary?.counts?.in_progress, load]);

  // One-shot soft poll of /upcoming while cache warms — updates the
  // banner only, leaves the articles table alone.
  useEffect(() => {
    if (!upcoming?.cache_warming) return;
    let cancelled = false;
    let attempts = 0;
    const t = setInterval(async () => {
      if (cancelled || document.visibilityState !== "visible") return;
      attempts += 1;
      if (attempts > 8) {
        clearInterval(t);
        return;
      }
      try {
        const up = await API.get("/admin/auto-article/upcoming", {
          params: { limit: 2 },
        });
        if (!cancelled) setUpcoming(up.data || null);
        if (!up.data?.cache_warming) clearInterval(t);
      } catch {
        /* ignore */
      }
    }, 15_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [upcoming?.cache_warming]);

  const counts = summary?.counts || {};

  return (
    <div className="space-y-5">
      {/* Status summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="aa-status-tiles">
        <StatusCountTile
          label="Published" count={counts.published || 0}
          tone="ok" icon={<CheckCircle size={16} weight="fill" />}
          active={filter.status === "published"}
          onClick={() => setFilter((f) => ({ ...f, status: f.status === "published" ? "" : "published" }))}
          testId="aa-tile-published"
        />
        <StatusCountTile
          label="Pending approval" count={counts.pending_approval || 0}
          tone="warn" icon={<Warning size={16} weight="fill" />}
          active={filter.status === "pending_approval"}
          onClick={() => setFilter((f) => ({ ...f, status: f.status === "pending_approval" ? "" : "pending_approval" }))}
          testId="aa-tile-pending-approval"
        />
        <StatusCountTile
          label="Failed" count={counts.failed || 0}
          tone="fail" icon={<Warning size={16} weight="fill" />}
          active={filter.status === "failed"}
          onClick={() => setFilter((f) => ({ ...f, status: f.status === "failed" ? "" : "failed" }))}
          testId="aa-tile-failed"
        />
        <StatusCountTile
          label="In progress" count={counts.in_progress || 0}
          tone="info" icon={<ArrowsClockwise size={16} weight="fill" className={counts.in_progress > 0 ? "animate-spin" : ""} />}
          active={filter.status === "in_progress"}
          onClick={() => setFilter((f) => ({ ...f, status: f.status === "in_progress" ? "" : "in_progress" }))}
          testId="aa-tile-in-progress"
        />
        <StatusCountTile
          label="Skipped" count={counts.skipped || 0}
          tone="warn" icon={<X size={16} weight="bold" />}
          active={filter.status === "skipped"}
          onClick={() => setFilter((f) => ({ ...f, status: f.status === "skipped" ? "" : "skipped" }))}
          testId="aa-tile-skipped"
        />
      </div>

      {/* Recent failure banner */}
      {(summary?.recent_failures || []).length > 0 && filter.status !== "failed" && (
        <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-4 text-xs" data-testid="aa-recent-failures">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-rose-800 flex items-center gap-1.5">
              <Warning size={14} weight="fill" /> {summary.recent_failures.length} recent failure{summary.recent_failures.length === 1 ? "" : "s"}
            </span>
            <button onClick={() => setFilter((f) => ({ ...f, status: "failed" }))} className="text-rose-700 underline hover:text-rose-900">
              View all failed
            </button>
          </div>
          <ul className="space-y-1">
            {summary.recent_failures.slice(0, 3).map((f) => (
              <li key={f.conversion_id} className="flex items-baseline gap-2">
                <span className="text-rose-600 font-mono">[{f.error_stage || "unknown"}]</span>
                <span className="font-medium text-slate-800">{f.brand_name || f.brand_id}</span>
                <span className="text-rose-700 truncate flex-1">{f.error_reason || "(no detail)"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Coming-up-next pipeline preview */}
      <UpcomingPipelinePanel upcoming={upcoming} onRunPoll={runPoll} polling={polling} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <FilterChip label="All types" active={!filter.article_type} onClick={() => setFilter((f) => ({ ...f, article_type: "" }))} />
          <FilterChip label="Standard" active={filter.article_type === "standard"} onClick={() => setFilter((f) => ({ ...f, article_type: "standard" }))} />
          <FilterChip label="Seasonal" active={filter.article_type === "seasonal"} onClick={() => setFilter((f) => ({ ...f, article_type: "seasonal" }))} />
          <span className="hidden sm:inline text-slate-300 px-1">|</span>
          <FilterChip label="All status" active={!filter.status} onClick={() => setFilter((f) => ({ ...f, status: "" }))} />
          <FilterChip label="Published" active={filter.status === "published"} onClick={() => setFilter((f) => ({ ...f, status: "published" }))} />
          <FilterChip label="Pending approval" active={filter.status === "pending_approval"} onClick={() => setFilter((f) => ({ ...f, status: "pending_approval" }))} />
          <FilterChip label="Failed" active={filter.status === "failed"} onClick={() => setFilter((f) => ({ ...f, status: "failed" }))} />
          <FilterChip label="In progress" active={filter.status === "in_progress"} onClick={() => setFilter((f) => ({ ...f, status: "in_progress" }))} />
          <FilterChip label="Skipped" active={filter.status === "skipped"} onClick={() => setFilter((f) => ({ ...f, status: "skipped" }))} />
          <span className="hidden sm:inline text-slate-300 px-1">|</span>
          <FilterChip label="All quality" active={!filter.editor_quality} onClick={() => setFilter((f) => ({ ...f, editor_quality: "" }))} />
          <FilterChip label="Force-published" active={filter.editor_quality === "forced"} onClick={() => setFilter((f) => ({ ...f, editor_quality: "forced" }))} />
          <FilterChip label="After retry" active={filter.editor_quality === "after_retry"} onClick={() => setFilter((f) => ({ ...f, editor_quality: "after_retry" }))} />
        </div>
        <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto">
          <span className="text-xs text-slate-500 whitespace-nowrap">{rows.length} articles</span>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} data-testid="aa-articles-refresh-btn">
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />Refresh
          </Button>
        </div>
      </div>
      <ArticlesTable rows={rows} loading={loading} onOpenDetail={setDetail} onRetry={() => load()} />

      <ArticleDetailDrawer
        conversionId={detail}
        onClose={() => setDetail(null)}
        onRetried={() => load()}
      />
    </div>
  );
}

function StatusCountTile({ label, count, tone, icon, active, onClick, testId }) {
  const toneClass = {
    ok: active ? "bg-emerald-600 text-white border-emerald-700" : "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100",
    fail: active ? "bg-rose-600 text-white border-rose-700" : "bg-rose-50 border-rose-200 text-rose-800 hover:bg-rose-100",
    info: active ? "bg-indigo-600 text-white border-indigo-700" : "bg-indigo-50 border-indigo-200 text-indigo-800 hover:bg-indigo-100",
    warn: active ? "bg-amber-500 text-white border-amber-600" : "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100",
  }[tone];
  return (
    <button onClick={onClick} className={`rounded-lg border px-4 py-3 text-left transition-colors ${toneClass}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider font-bold opacity-80">{label}</span>
        <span className="opacity-80">{icon}</span>
      </div>
      <div className="text-2xl font-bold tabular-nums">{count.toLocaleString()}</div>
    </button>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1 rounded-full border transition-colors ${
        active
          ? "bg-indigo-600 text-white border-indigo-600"
          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
      }`}
    >
      {label}
    </button>
  );
}

// ─── Coming-up-next pipeline preview ──────────────────────────────
// Shows the operator what the auto-poller will pick up next (max 2
// slots: "writing now" + "next in queue"). Removes the guesswork about
// whether the pipeline is alive between polling ticks.

function UpcomingPipelinePanel({ upcoming, onRunPoll, polling }) {
  if (!upcoming) return null;
  const writing = upcoming.writing_now || [];
  const next = upcoming.next_up || [];
  const totalPending = upcoming.total_pending_pairs || 0;

  // While the heavy aggregation cache is warming up (first 60-90s
  // after a backend restart), show a friendly loading state.
  if (upcoming.cache_warming && writing.length === 0 && next.length === 0) {
    return (
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 px-4 py-3.5 text-xs" data-testid="aa-upcoming-warming">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start sm:items-center gap-2 text-indigo-800 min-w-0">
            <ArrowsClockwise size={14} className="animate-spin shrink-0 mt-0.5 sm:mt-0" />
            <div className="min-w-0">
              <span className="font-semibold">Warming pipeline preview…</span>
              <span className="text-indigo-600 ml-1.5">Calculating next picks (~60s on first load)</span>
            </div>
          </div>
          {onRunPoll && (
            <Button
              size="sm"
              variant="default"
              onClick={onRunPoll}
              disabled={polling}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8 shrink-0 self-start sm:self-auto"
              data-testid="aa-run-poll-now-warming"
            >
              {polling ? "Running…" : "Run polling now →"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (writing.length === 0 && next.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3.5 text-xs text-slate-600" data-testid="aa-upcoming-empty">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <span className="font-semibold text-slate-700">Pipeline idle.</span> No qualifying (brand × publisher) pairs in the next-up queue. The system polls hourly; new conversions ≥ ${upcoming.threshold_usd?.toFixed?.(0) || 10} will auto-enqueue.
          </div>
          {onRunPoll && (
            <Button
              size="sm"
              variant="outline"
              onClick={onRunPoll}
              disabled={polling}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-xs h-8 whitespace-nowrap shrink-0 self-start sm:self-auto"
              data-testid="aa-run-poll-now-empty"
            >
              {polling ? "Running…" : "Run polling now →"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50/70 to-slate-50/30 px-4 py-3.5" data-testid="aa-upcoming-panel">
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="text-xs font-bold text-indigo-900 uppercase tracking-wider flex items-center gap-1.5">
          <ArrowsClockwise size={13} className={writing.length > 0 ? "animate-spin" : ""} weight="bold" />
          Pipeline · Coming up next
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-slate-600">
            {writing.length > 0 && <span className="text-indigo-700 font-semibold">{writing.length} writing</span>}
            {writing.length > 0 && next.length > 0 && <span className="mx-1 text-slate-400">·</span>}
            {next.length > 0 && <span>{next.length} queued</span>}
            {totalPending > next.length && <span className="text-slate-500"> · {totalPending} pending total</span>}
          </span>
          {onRunPoll && (
            <Button
              size="sm"
              variant="default"
              onClick={onRunPoll}
              disabled={polling}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-6 px-2"
              data-testid="aa-run-poll-now"
            >
              {polling ? "Running…" : "Run polling now"}
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {writing.map((w, i) => (
          <UpcomingCard
            key={`writing-${w.conversion_id || i}`}
            slot="writing"
            brandName={w.brand_name || w.brand_id}
            publisher={w.publisher_email || w.publisher_id?.slice(0, 12) + "…"}
            network={w.network}
            startedAt={w.started_at}
          />
        ))}
        {next.map((n, i) => (
          <UpcomingCard
            key={`next-${n.brand_id}-${n.publisher_id}-${i}`}
            slot={n.throttled ? "throttled" : "queued"}
            brandName={n.brand_name || n.brand_id}
            publisher={n.publisher_email || n.publisher_id?.slice(0, 12) + "…"}
            network={n.network}
            grossUsd={n.cumulative_gross_usd}
            target={n.target_articles}
            existing={n.existing_articles}
            nextEligibleAt={n.next_eligible_at}
          />
        ))}
      </div>
      <p className="text-[10px] text-slate-500 mt-2">
        Auto-publishes hourly. Max 1 article per (brand × publisher) per 7 days — pairs in cooldown show their next eligible time.
      </p>
    </div>
  );
}

function UpcomingCard({ slot, brandName, publisher, network, grossUsd, target, existing, startedAt, nextEligibleAt }) {
  const cfg = {
    writing: {
      label: "Writing now",
      labelClass: "text-indigo-700 bg-indigo-100",
      borderClass: "border-indigo-300 bg-white",
      icon: <ArrowsClockwise size={11} className="animate-spin" />,
    },
    queued: {
      label: "Next up",
      labelClass: "text-emerald-700 bg-emerald-100",
      borderClass: "border-emerald-200 bg-white",
      icon: <CheckCircle size={11} weight="fill" />,
    },
    throttled: {
      label: "Cooldown",
      labelClass: "text-amber-700 bg-amber-100",
      borderClass: "border-amber-200 bg-amber-50/50",
      icon: <Warning size={11} weight="fill" />,
    },
  }[slot];
  const dataTestId = `aa-upcoming-card-${slot}`;

  return (
    <div className={`rounded-md border ${cfg.borderClass} p-2.5 text-xs`} data-testid={dataTestId}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold ${cfg.labelClass}`}>
          {cfg.icon}
          {cfg.label}
        </span>
        {network && (
          <Badge variant="outline" className="border-slate-300 text-slate-700 bg-white font-mono text-[9px] uppercase tracking-wider px-1.5 py-0">
            {network}
          </Badge>
        )}
      </div>
      <div className="font-semibold text-slate-900 truncate">{brandName || "—"}</div>
      <div className="text-slate-600 text-[11px] truncate">{publisher || "—"}</div>
      <div className="mt-1.5 flex items-center gap-3 text-[10px]">
        {grossUsd != null && (
          <span className="text-emerald-700 font-medium tabular-nums">${grossUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
        )}
        {target != null && (
          <span className="text-slate-500">
            <span className="tabular-nums">{existing}</span> / <span className="tabular-nums font-semibold">{target}</span> articles
          </span>
        )}
      </div>
      {nextEligibleAt && (
        <div className="text-[10px] text-amber-700 mt-1">
          Eligible after {fmtDateShort(nextEligibleAt)}
        </div>
      )}
      {startedAt && slot === "writing" && (
        <div className="text-[10px] text-indigo-600 mt-1 italic">
          Started {fmtDateShort(startedAt)}
        </div>
      )}
    </div>
  );
}

// ─── Shared components ────────────────────────────────────────────

function StatusTile({ label, value, tone, icon, sub, "data-testid": testId }) {
  const toneClass = {
    ok: "bg-emerald-50 border-emerald-200 text-emerald-900",
    warn: "bg-amber-50 border-amber-200 text-amber-900",
    neutral: "bg-white border-slate-200 text-slate-900",
  }[tone || "neutral"];
  return (
    <div className={`rounded-lg border p-4 ${toneClass}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[11px] uppercase tracking-wider font-bold opacity-70">{label}</span>
        <span className="opacity-70">{icon}</span>
      </div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function CredChip({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`} data-testid={`aa-cred-${label.toLowerCase()}`}>
      {ok ? <CheckCircle size={12} weight="fill" /> : <Warning size={12} weight="fill" />}
      <span className="font-mono">{label}</span>
      <span className="opacity-70">{ok ? "set" : "missing"}</span>
    </span>
  );
}

function ArticlesTable({ rows, title, subtitle, compact = false, loading = false, onOpenDetail, onRetry }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      {title && (
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-sm text-slate-900">{title}</h2>
          {subtitle && <span className="text-xs text-slate-500">{subtitle}</span>}
        </div>
      )}
      {loading ? (
        <p className="px-4 py-12 text-center text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="px-4 py-12 text-center text-sm text-slate-500">No articles yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
              <tr>
                <th className="px-4 py-2 text-left">Published</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Quality</th>
                {!compact && <th className="px-4 py-2 text-left">Network</th>}
                {!compact && <th className="px-4 py-2 text-left">Brand</th>}
                {!compact && <th className="px-4 py-2 text-left">Publisher</th>}
                <th className="px-4 py-2 text-left">Article / Error</th>
                {!compact && <th className="px-4 py-2 text-center">Banners</th>}
                {!compact && <th className="px-4 py-2 text-left">Disclosure</th>}
                <th className="px-4 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((a) => (
                <ArticleRow
                  key={a.conversion_id}
                  a={a}
                  compact={compact}
                  onOpenDetail={onOpenDetail}
                  onRetry={onRetry}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ArticleRow({ a, compact = false, onOpenDetail, onRetry }) {
  // Status derivation: explicit `status` field first, then legacy fallback
  const status =
    a.status ||
    (a.wp_post_id ? "published" :
     (a.skipped && a.error_stage) ? "failed" :
     a.skipped ? "skipped" : "unknown");
  const bannerCount = [a.banner_300x250_url, a.banner_728x90_url, a.banner_160x600_url].filter(Boolean).length;
  const postUrl = a.wp_post_url || (a.wp_post_id && a.wp_site ? `${a.wp_site}/?p=${a.wp_post_id}` : null);
  const [retrying, setRetrying] = useState(false);
  const [disclosureBusy, setDisclosureBusy] = useState(false);

  const handleFixDisclosure = async (recheck = false) => {
    const verb = recheck ? "Re-check disclosure on" : "Add disclosure to";
    if (!window.confirm(`${verb} "${a.article_title || a.brand_name}"? This PATCHes the WP post in place.`)) return;
    setDisclosureBusy(true);
    try {
      const r = await API.post(
        `/admin/auto-article/articles/${encodeURIComponent(a.conversion_id)}/fix-disclosure`,
        null,
        { params: { dry_run: false }, timeout: 120_000 },
      );
      const st = r.data.disclosure_status;
      const row = r.data.row || {};
      if (row.error || row.skipped) {
        toast.error(row.error || row.skipped || "Disclosure update failed");
      } else if (row.status === "patched" || st === "present") {
        toast.success(recheck ? "Disclosure verified / updated" : "Disclosure added");
      } else if (row.status === "already_has") {
        toast.success("Disclosure already present — marked OK");
      } else {
        toast.success(`Done (${st || "ok"})`);
      }
      onRetry?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disclosure update failed");
    } finally {
      setDisclosureBusy(false);
    }
  };

  const handleRetry = async () => {
    if (!window.confirm(`Retry article for ${a.brand_name || a.brand_id}? This will re-run scrape → write → publish.`)) return;
    setRetrying(true);
    try {
      const r = await API.post(`/admin/auto-article/articles/${encodeURIComponent(a.conversion_id)}/retry`);
      if (r.data.published) {
        toast.success(`Retry succeeded — published as ${r.data.article_title || `post #${r.data.wp_post_id}`}`);
      } else {
        toast.error(`Retry failed at [${r.data.error_stage}]: ${r.data.error_reason}`);
      }
      onRetry?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
    finally { setRetrying(false); }
  };

  // Force-requeue a stuck in_progress row. Drops it into the
  // sequential retry queue rather than firing the pipeline inline
  // (consistent with how Reprocess Stuck works in the toolbar).
  const handleReprocess = async () => {
    if (!window.confirm(`Reprocess stuck article ${a.brand_name || a.brand_id}? It will be queued and processed sequentially.`)) return;
    setRetrying(true);
    try {
      const r = await API.post(`/admin/auto-article/articles/${encodeURIComponent(a.conversion_id)}/reprocess`);
      if (r.data.accepted) toast.success("Queued for reprocess");
      else toast.error("Already queued or invalid state");
      onRetry?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reprocess failed"); }
    finally { setRetrying(false); }
  };

  // Regenerate Hugging Face images for a published article
  // (hero + mid_1 + mid_2). The WP post is patched in place — slug,
  // URL, comments, indexing all preserved. Used when an admin spots
  // a broken / corrupt / off-brand image post-publish.
  const handleRegenImages = async () => {
    if (!window.confirm(`Regenerate all images for "${a.article_title || a.brand_name}"?\n\nRe-resolves scraped brand photos (no AI lifestyle fillers) and patches the WordPress post in place. Takes ~1–2 minutes.`)) return;
    setRetrying(true);
    try {
      const r = await API.post(
        `/admin/auto-article/articles/${encodeURIComponent(a.conversion_id)}/regenerate-images`,
        {},
        { timeout: 300_000 },
      );      const n = Object.keys(r.data.regenerated || {}).length;
      const f = Object.keys(r.data.failed || {}).length;
      if (n && !f) toast.success(`Regenerated ${n} image(s). WP post #${r.data.wp_post_id} patched.`);
      else if (n) toast(`Regenerated ${n}, ${f} failed — check detail drawer.`);
      else toast.error(`All ${f} image slots failed to regenerate`);
      onRetry?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Regenerate failed"); }
    finally { setRetrying(false); }
  };

  return (
    <tr className="hover:bg-slate-50/50 transition-colors" data-testid={`aa-article-row-${a.conversion_id}`}>
      <td className="px-4 py-2 text-slate-700 whitespace-nowrap font-mono text-xs">
        {fmtDate(a.published_at)}
        {a.last_refreshed && (
          <div className="text-[10px] text-indigo-600 mt-0.5 normal-case font-sans" title={`Last refreshed: ${a.last_refreshed}`}>
            refreshed {fmtDate(a.last_refreshed)}
          </div>
        )}
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        <StatusBadge status={status} />
        {a.article_type && a.article_type !== "standard" && status === "published" && (
          <Badge variant="outline" className="ml-1 border-indigo-300 text-indigo-700 bg-indigo-50 text-[10px]">{a.article_type}</Badge>
        )}
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        <QualityBadge quality={a.editor_quality} feedback={a.editor_feedback} status={status} />
      </td>
      {!compact && (
        <td className="px-4 py-2 whitespace-nowrap" data-testid={`aa-article-network-${a.conversion_id}`}>
          {a.network ? (
            <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50 font-mono text-[10px] uppercase tracking-wider">
              {a.network}
            </Badge>
          ) : (
            <span className="text-slate-400 text-xs">—</span>
          )}
        </td>
      )}
      {!compact && <td className="px-4 py-2 text-slate-900 font-medium">{a.brand_name || a.brand_id || "—"}</td>}
      {!compact && <td className="px-4 py-2 text-slate-600 text-xs">{a.publisher_email || (<span className="text-slate-400 font-mono">{(a.publisher_id || "").slice(0, 12)}…</span>)}</td>}
      <td className="px-4 py-2 max-w-md">
        {status === "failed" ? (
          <div className="flex flex-col gap-0.5">
            <span className="inline-flex items-center gap-1.5 text-[11px]">
              <Badge variant="outline" className="border-rose-300 text-rose-700 bg-rose-50 font-mono">stage: {a.error_stage || "unknown"}</Badge>
            </span>
            <span className="text-xs text-rose-700 line-clamp-2" title={a.error_reason || ""}>{a.error_reason || a.skipped_reason || "(no reason captured)"}</span>
          </div>
        ) : status === "skipped" ? (
          <span className="text-xs text-amber-700 line-clamp-2" title={a.skipped_reason || ""}>{a.skipped_reason || "(no reason)"}</span>
        ) : status === "in_progress" ? (
          <span className="text-xs text-indigo-700 italic">Running pipeline…</span>
        ) : postUrl ? (
          <a href={postUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline text-xs" title={a.article_title || ""}>
            <ArrowSquareOut size={12} />
            <span className="truncate max-w-xs">{a.article_title || `Post #${a.wp_post_id}`}</span>
          </a>
        ) : <span className="text-slate-400 italic text-xs">—</span>}
      </td>
      {!compact && (
        <td className="px-4 py-2 text-center whitespace-nowrap">
          {bannerCount > 0 ? (
            <div className="inline-flex items-center gap-1">
              {[
                { key: "300x250", url: a.banner_300x250_url },
                { key: "728x90", url: a.banner_728x90_url },
                { key: "160x600", url: a.banner_160x600_url },
              ].filter((b) => b.url).map((b) => (
                <a key={b.key} href={b.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 text-[10px] text-slate-700 font-mono" title={`Open ${b.key}`}>
                  <ImageIcon size={10} />{b.key}
                </a>
              ))}
            </div>
          ) : <span className="text-slate-300 text-xs">—</span>}
        </td>
      )}
      {!compact && (
        <td className="px-4 py-2 whitespace-nowrap" data-testid={`aa-disclosure-${a.conversion_id}`}>
          <DisclosureStatusCell
            a={a}
            status={status}
            busy={disclosureBusy}
            onAdd={() => handleFixDisclosure(false)}
            onRecheck={() => handleFixDisclosure(true)}
          />
        </td>
      )}
      <td className="px-4 py-2 text-center whitespace-nowrap">
        <div className="inline-flex items-center gap-1">
          {status === "failed" && (
            <button
              onClick={() => onOpenDetail?.(a.conversion_id)}
              className="text-[11px] px-2 py-0.5 rounded border border-rose-200 text-rose-700 bg-white hover:bg-rose-50"
              data-testid={`aa-view-error-${a.conversion_id}`}
              title="View full error trace"
            >View error</button>
          )}
          {(status === "failed" || status === "skipped" || status === "image_error") && (
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="text-[11px] px-2 py-0.5 rounded border border-indigo-200 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50"
              data-testid={`aa-retry-${a.conversion_id}`}
              title="Re-run the pipeline for this conversion"
            >{retrying ? "…" : "Retry"}</button>
          )}
          {status === "in_progress" && (
            <button
              onClick={handleReprocess}
              disabled={retrying}
              className="text-[11px] px-2 py-0.5 rounded border border-amber-200 text-amber-700 bg-white hover:bg-amber-50 disabled:opacity-50"
              data-testid={`aa-reprocess-${a.conversion_id}`}
              title="Force-requeue this stuck article (server restart / crash recovery)"
            >{retrying ? "…" : "Reprocess"}</button>
          )}
          {status === "pending_approval" && (
            <>
              <button
                onClick={async () => {
                  if (!window.confirm(`Approve & publish "${a.article_title || a.brand_name}" to WordPress?`)) return;
                  setRetrying(true);
                  try {
                    await API.post(`/admin/auto-article/articles/${a.conversion_id}/approve`);
                    toast.success("Approved and published");
                    onRetried?.();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Approve failed");
                  } finally {
                    setRetrying(false);
                  }
                }}
                disabled={retrying}
                className="text-[11px] px-2 py-0.5 rounded border border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50"
                data-testid={`aa-approve-${a.conversion_id}`}
              >{retrying ? "…" : "Approve"}</button>
              <button
                onClick={async () => {
                  if (!window.confirm(`Reject draft for "${a.article_title || a.brand_name}"?`)) return;
                  setRetrying(true);
                  try {
                    await API.post(`/admin/auto-article/articles/${a.conversion_id}/reject`);
                    toast.success("Rejected");
                    onRetried?.();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Reject failed");
                  } finally {
                    setRetrying(false);
                  }
                }}
                disabled={retrying}
                className="text-[11px] px-2 py-0.5 rounded border border-rose-200 text-rose-700 bg-white hover:bg-rose-50 disabled:opacity-50"
                data-testid={`aa-reject-${a.conversion_id}`}
              >Reject</button>
            </>
          )}
          {(status === "published" || status === "image_error") && (
            <button
              onClick={handleRegenImages}
              disabled={retrying}
              className="text-[11px] px-2 py-0.5 rounded border border-purple-200 text-purple-700 bg-white hover:bg-purple-50 disabled:opacity-50"
              data-testid={`aa-regen-images-${a.conversion_id}`}
              title="Re-run Hugging Face image generation + patch the WP post in place (no full republish)"
            >{retrying ? "…" : "Regen images"}</button>
          )}
          {/* Details opens drawer for ALL statuses — admin always needs
              a way to inspect the row. Previously this button only
              showed on `published` and `failed`, which made the
              View Error / Details flow inconsistent. */}
          <button
            onClick={() => onOpenDetail?.(a.conversion_id)}
            className="text-[11px] px-2 py-0.5 rounded border border-slate-200 text-slate-600 bg-white hover:bg-slate-50"
            data-testid={`aa-view-detail-${a.conversion_id}`}
          >Details</button>
        </div>
      </td>
    </tr>
  );
}

function DisclosureStatusCell({ a, status, busy, onAdd, onRecheck }) {
  if (status !== "published" || !a.wp_post_id) {
    return <span className="text-slate-300 text-xs">—</span>;
  }
  const ds = a.disclosure_status || "missing";
  const map = {
    present: { cls: "border-emerald-200 text-emerald-700 bg-emerald-50", label: "Present" },
    missing: { cls: "border-amber-200 text-amber-800 bg-amber-50", label: "Missing" },
    outdated: { cls: "border-orange-200 text-orange-800 bg-orange-50", label: "Outdated" },
    processing: { cls: "border-indigo-200 text-indigo-700 bg-indigo-50 animate-pulse", label: "Processing" },
  };
  const cfg = map[ds] || map.missing;
  const typeLabel = a.expected_disclosure_type === "amazon" ? "Amazon" : "Generic";
  return (
    <div className="flex flex-col gap-1 items-start">
      <Badge variant="outline" className={`${cfg.cls} text-[10px]`} title={a.affiliate_disclosure_at ? `Logged ${a.affiliate_disclosure_at}` : typeLabel}>
        {cfg.label}
      </Badge>
      <span className="text-[10px] text-slate-500">{typeLabel}</span>
      <div className="flex flex-wrap gap-1">
        {(ds === "missing" || ds === "outdated") && (
          <button
            type="button"
            onClick={onAdd}
            disabled={busy}
            className="text-[10px] px-1.5 py-0.5 rounded border border-amber-300 text-amber-800 bg-white hover:bg-amber-50 disabled:opacity-50"
            data-testid={`aa-add-disclosure-${a.conversion_id}`}
          >
            {busy ? "…" : "Add"}
          </button>
        )}
        {(ds === "present" || ds === "outdated") && (
          <button
            type="button"
            onClick={onRecheck}
            disabled={busy}
            className="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
            data-testid={`aa-recheck-disclosure-${a.conversion_id}`}
          >
            {busy ? "…" : "Recheck"}
          </button>
        )}
        {ds === "processing" && (
          <span className="text-[10px] text-indigo-600">Auto worker…</span>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    published: { cls: "border-emerald-300 text-emerald-700 bg-emerald-50", label: "Published" },
    pending_approval: { cls: "border-amber-300 text-amber-800 bg-amber-50", label: "Pending approval" },
    failed: { cls: "border-rose-300 text-rose-700 bg-rose-50", label: "Failed" },
    in_progress: { cls: "border-indigo-300 text-indigo-700 bg-indigo-50 animate-pulse", label: "In progress" },
    queued: { cls: "border-amber-300 text-amber-700 bg-amber-50", label: "Queued" },
    image_error: { cls: "border-orange-300 text-orange-700 bg-orange-50", label: "Image error" },
    skipped: { cls: "border-amber-300 text-amber-700 bg-amber-50", label: "Skipped" },
    rejected: { cls: "border-slate-300 text-slate-600 bg-slate-50", label: "Rejected" },
    unknown: { cls: "border-slate-300 text-slate-600 bg-slate-50", label: "Unknown" },
  };
  const cfg = map[status] || map.unknown;
  return <Badge variant="outline" className={cfg.cls}>{cfg.label}</Badge>;
}

function QualityBadge({ quality, feedback, status }) {
  // Published + pending_approval rows can show a quality verdict
  if (status !== "published" && status !== "pending_approval") {
    return <span className="text-slate-300 text-xs">—</span>;
  }
  const map = {
    first_try: {
      cls: "border-emerald-300 text-emerald-700 bg-emerald-50",
      label: "Editor approved",
      icon: <CheckCircle size={10} weight="fill" />,
    },
    after_retry: {
      cls: "border-amber-300 text-amber-700 bg-amber-50",
      label: "Approved on retry",
      icon: <ArrowsClockwise size={10} weight="bold" />,
    },
    qa_passed: {
      cls: "border-emerald-300 text-emerald-700 bg-emerald-50",
      label: "QA passed",
      icon: <CheckCircle size={10} weight="fill" />,
    },
    human_approved: {
      cls: "border-emerald-300 text-emerald-800 bg-emerald-50",
      label: "Human approved",
      icon: <CheckCircle size={10} weight="fill" />,
    },
    forced: {
      cls: "border-rose-300 text-rose-700 bg-rose-50",
      label: "Force-published",
      icon: <Warning size={10} weight="fill" />,
    },
    unreviewed: {
      cls: "border-slate-200 text-slate-500 bg-slate-50",
      label: "Unreviewed",
      icon: null,
    },
  };
  const cfg = map[quality] || map.unreviewed;
  return (
    <span title={feedback || cfg.label} className="inline-block">
      <Badge variant="outline" className={`${cfg.cls} text-[10px] inline-flex items-center gap-1`}>
        {cfg.icon}{cfg.label}
      </Badge>
    </span>
  );
}

function ArticleDetailDrawer({ conversionId, onClose, onRetried }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [history, setHistory] = useState([]);
  const [editUrls, setEditUrls] = useState([""]);
  const [editInstructions, setEditInstructions] = useState("");
  const [aiEditing, setAiEditing] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [recheckResult, setRecheckResult] = useState(null);
  const [feedbackReason, setFeedbackReason] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    if (!conversionId) { setData(null); setHistory([]); return; }
    setLoading(true);
    setEditUrls([""]);
    setEditInstructions("");
    setRecheckResult(null);
    setFeedbackReason("");
    Promise.all([
      API.get(`/admin/auto-article/articles/${encodeURIComponent(conversionId)}`),
      API.get(`/admin/auto-article/articles/${encodeURIComponent(conversionId)}/retry-history`).catch(() => ({ data: { attempts: [] } })),
    ])
      .then(([r, h]) => {
        setData(r.data);
        setHistory(h.data?.attempts || []);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load detail"))
      .finally(() => setLoading(false));
  }, [conversionId]);

  const handleRetry = async () => {
    if (!data) return;
    setRetrying(true);
    try {
      const r = await API.post(`/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}/retry`);
      if (r.data.published) toast.success(`Retry succeeded`);
      else toast.error(`Retry failed at [${r.data.error_stage}]: ${r.data.error_reason}`);
      // Reload detail + history
      const [r2, h2] = await Promise.all([
        API.get(`/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}`),
        API.get(`/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}/retry-history`).catch(() => ({ data: { attempts: [] } })),
      ]);
      setData(r2.data);
      setHistory(h2.data?.attempts || []);
      onRetried?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
    finally { setRetrying(false); }
  };

  const handleRecheck = async () => {
    if (!data) return;
    setRechecking(true);
    try {
      const r = await API.post(
        `/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}/recheck`,
        {},
        { timeout: 120_000 },
      );
      setRecheckResult(r.data);
      if (r.data.passed) {
        toast.success("Recheck passed — no format or spelling issues found");
      } else {
        toast.warning(
          `Recheck found ${(r.data.issues || []).length} issue(s)`,
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recheck failed");
    } finally {
      setRechecking(false);
    }
  };

  const applyRecheckFix = () => {
    if (!recheckResult?.fix_instructions_preview) return;
    setEditInstructions(recheckResult.fix_instructions_preview);
    toast.info("Fix instructions loaded into AI edit — review and click Update");
  };

  const handleAiEdit = async () => {
    if (!data) return;
    const instr = editInstructions.trim();
    if (!instr) {
      toast.error("Enter AI instructions describing what to change");
      return;
    }
    setAiEditing(true);
    try {
      const payload = {
        instructions: instr,
        ...buildAiExtrasPayload(editUrls, ""),
      };
      const r = await API.post(
        `/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}/ai-edit`,
        payload,
        { timeout: 180_000 },
      );
      toast.success(r.data.changes_summary || "Article updated with AI");
      if (r.data.reference_errors?.length) {
        toast.warning(
          `${r.data.reference_errors.length} reference URL(s) failed — edit continued without them`,
        );
      }
      setEditInstructions("");
      setEditUrls([""]);
      const [r2, h2] = await Promise.all([
        API.get(`/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}`),
        API.get(`/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}/retry-history`).catch(() => ({ data: { attempts: [] } })),
      ]);
      setData(r2.data);
      setHistory(h2.data?.attempts || []);
      onRetried?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI update failed");
    } finally {
      setAiEditing(false);
    }
  };

  const handleFeedback = async (rating) => {
    if (!data) return;
    if (rating === "down" && !feedbackReason.trim()) {
      toast.error("Add a short reason when rating thumbs down");
      return;
    }
    setSubmittingFeedback(true);
    try {
      await API.post(
        `/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}/feedback`,
        {
          rating,
          rejection_reason: rating === "down" ? feedbackReason.trim() : "",
        },
      );
      toast.success(
        rating === "up"
          ? "Thanks — saved for future articles"
          : "Feedback saved — we'll avoid this pattern",
      );
      setFeedbackReason("");
      const r2 = await API.get(
        `/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}`,
      );
      setData(r2.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  return (
    <Sheet open={!!conversionId} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="aa-article-detail-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {data?.brand_name || "Article details"}
            {data && <StatusBadge status={data.status} />}
          </SheetTitle>
          <SheetDescription className="font-mono text-xs">{conversionId}</SheetDescription>
        </SheetHeader>

        {loading ? (
          <p className="text-sm text-slate-500 mt-6">Loading…</p>
        ) : data ? (
          <div className="space-y-5 mt-6">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <DetailField label="Brand" value={data.brand_name || data.brand_id} />
              <DetailField label="Network" value={data.network} />
              <DetailField label="Publisher" value={data.publisher_email} />
              <DetailField label="Started" value={fmtDate(data.started_at)} />
              <DetailField label="Last update" value={fmtDate(data.failed_at || data.published_at)} />
              <DetailField label="Article type" value={data.article_type || "standard"} />
            </div>

            {data.status === "failed" && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
                <h3 className="text-sm font-semibold text-rose-900 mb-2 flex items-center gap-2">
                  <Warning size={14} weight="fill" /> Failed at stage: <code className="bg-white px-1.5 py-0.5 rounded border border-rose-200">{data.error_stage || "unknown"}</code>
                </h3>
                <p className="text-xs text-rose-800 mb-3"><strong>Reason:</strong> {data.error_reason || "(no reason)"}</p>
                {data.error_trace && (
                  <details className="text-[11px]" data-testid="aa-detail-trace">
                    <summary className="cursor-pointer text-rose-700 font-medium">Full traceback</summary>
                    <pre className="mt-2 max-h-96 overflow-auto bg-white border border-rose-200 rounded p-2 font-mono text-rose-900 whitespace-pre-wrap break-words">{data.error_trace}</pre>
                  </details>
                )}
              </div>
            )}

            {(data.status === "pending_approval" || data.status === "published") && data.editor_quality && data.editor_quality !== "unreviewed" && (
              <div className={`rounded-lg border p-4 ${
                data.editor_quality === "qa_passed" || data.editor_quality === "human_approved" || data.editor_quality === "first_try"
                  ? "border-emerald-200 bg-emerald-50/50"
                  : data.editor_quality === "after_retry" ? "border-amber-200 bg-amber-50/50"
                  : "border-rose-200 bg-rose-50/50"
              }`} data-testid="aa-detail-editor-verdict">
                <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <QualityBadge quality={data.editor_quality} feedback={data.editor_feedback} status={data.status} />
                  <span className="text-slate-600 font-normal">— Editorial QA verdict</span>
                </h3>
                {data.editor_feedback && (
                  <p className="text-xs text-slate-700 whitespace-pre-wrap">{data.editor_feedback}</p>
                )}
                {data.article_html && data.status === "pending_approval" && (
                  <details className="mt-3 text-xs">
                    <summary className="cursor-pointer text-slate-700 font-medium">Preview HTML</summary>
                    <div
                      className="mt-2 max-h-96 overflow-auto bg-white border rounded p-3 prose prose-sm"
                      dangerouslySetInnerHTML={{ __html: data.article_html }}
                    />
                  </details>
                )}
              </div>
            )}

            {data.status === "pending_approval" && (
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    if (!window.confirm(`Approve & publish "${data.article_title || data.brand_name}"?`)) return;
                    setRetrying(true);
                    try {
                      await API.post(`/admin/auto-article/articles/${conversionId}/approve`);
                      toast.success("Approved and published");
                      onRetried?.();
                      onClose?.();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Approve failed");
                    } finally {
                      setRetrying(false);
                    }
                  }}
                  disabled={retrying}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="aa-detail-approve-btn"
                >
                  Approve & publish
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm("Reject this draft?")) return;
                    setRetrying(true);
                    try {
                      await API.post(`/admin/auto-article/articles/${conversionId}/reject`);
                      toast.success("Rejected");
                      onRetried?.();
                      onClose?.();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Reject failed");
                    } finally {
                      setRetrying(false);
                    }
                  }}
                  disabled={retrying}
                  data-testid="aa-detail-reject-btn"
                >
                  Reject
                </Button>
              </div>
            )}

            {data.status === "published" && (
              <div
                className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3"
                data-testid="aa-detail-feedback"
              >
                <h3 className="text-sm font-semibold text-slate-900">Article feedback</h3>
                <p className="text-[11px] text-slate-600">
                  Helps future articles for this brand learn your preferred style (RAG — no model fine-tuning).
                </p>
                {data.user_feedback?.rating && (
                  <p className="text-xs text-slate-700">
                    Last saved: <strong>{data.user_feedback.rating === "up" ? "Thumbs up" : "Thumbs down"}</strong>
                    {" · "}{fmtDate(data.user_feedback.created_at)}
                    {data.user_feedback.rejection_reason && (
                      <span className="block mt-1 text-slate-600">{data.user_feedback.rejection_reason}</span>
                    )}
                  </p>
                )}
                <textarea
                  className="w-full text-xs border border-slate-200 rounded-md p-2 min-h-[56px] bg-white"
                  placeholder="If thumbs down: what was wrong? (too generic, wrong industry, tone, etc.)"
                  value={feedbackReason}
                  onChange={(e) => setFeedbackReason(e.target.value)}
                  data-testid="aa-feedback-reason"
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={submittingFeedback}
                    onClick={() => handleFeedback("up")}
                    className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    data-testid="aa-feedback-up"
                  >
                    <ThumbsUp size={14} weight="fill" /> Thumbs up
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={submittingFeedback}
                    onClick={() => handleFeedback("down")}
                    className="border-rose-300 text-rose-800 hover:bg-rose-50"
                    data-testid="aa-feedback-down"
                  >
                    <ThumbsDown size={14} weight="fill" /> Thumbs down
                  </Button>
                </div>
              </div>
            )}

            {data.status === "published" && data.wp_post_url && (
              <a href={data.wp_post_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-indigo-700 hover:text-indigo-900 underline" data-testid="aa-detail-wp-link">
                <ArrowSquareOut size={14} /> {data.article_title || `Open WP post #${data.wp_post_id}`}
              </a>
            )}

            {data.tracking_url && (
              <DetailField label="Wrapped tracking URL" value={data.tracking_url} mono />
            )}

            {(data.status === "failed" || data.status === "skipped") && (
              <Button onClick={handleRetry} disabled={retrying} className="bg-indigo-600 hover:bg-indigo-700" data-testid="aa-detail-retry-btn">
                <ArrowsClockwise size={14} className={retrying ? "animate-spin" : ""} />Retry pipeline
              </Button>
            )}
            {(data.status === "published" || data.status === "image_error") && (
              <Button
                onClick={async () => {
                  if (!window.confirm(`Regenerate all images for "${data.article_title || data.brand_name}"?\n\nRe-resolves scraped brand photos (no AI lifestyle fillers) and patches the WordPress post in place. Slug + URL preserved.`)) return;
                  setRetrying(true);
                  try {
                    const r = await API.post(
                      `/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}/regenerate-images`,
                      {},
                      { timeout: 300_000 },
                    );                    const n = Object.keys(r.data.regenerated || {}).length;
                    const f = Object.keys(r.data.failed || {}).length;
                    if (n && !f) toast.success(`Regenerated ${n} image(s)`);
                    else if (n) toast(`${n} ok, ${f} failed`);
                    else toast.error("All slots failed");
                    // reload
                    const [r2, h2] = await Promise.all([
                      API.get(`/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}`),
                      API.get(`/admin/auto-article/articles/${encodeURIComponent(data.conversion_id)}/retry-history`).catch(() => ({ data: { attempts: [] } })),
                    ]);
                    setData(r2.data); setHistory(h2.data?.attempts || []);
                    onRetried?.();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Regenerate failed"); }
                  finally { setRetrying(false); }
                }}
                disabled={retrying || aiEditing}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="aa-detail-regen-images-btn"
              >
                <ArrowsClockwise size={14} className={retrying ? "animate-spin" : ""} />Regenerate images
              </Button>
            )}

            {(data.status === "published" || data.status === "image_error") && data.wp_post_id && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 space-y-3"
                data-testid="aa-detail-recheck"
              >
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Stethoscope size={14} className="text-amber-700" />
                  Recheck quality
                </h3>
                <p className="text-[11px] text-slate-600">
                  Verify typos, brand spelling, tracking links, and TopReviewsClub guide format on the live WordPress post.
                </p>
                <Button
                  onClick={handleRecheck}
                  disabled={rechecking || aiEditing}
                  variant="outline"
                  className="border-amber-300 text-amber-900 hover:bg-amber-100"
                  data-testid="aa-detail-recheck-btn"
                >
                  <ListChecks size={14} className={rechecking ? "animate-pulse" : ""} />
                  {rechecking ? "Checking…" : "Run recheck"}
                </Button>
                {recheckResult && (
                  <div className="space-y-2 text-xs">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant="outline"
                        className={
                          recheckResult.passed
                            ? "border-emerald-300 text-emerald-800 bg-emerald-50"
                            : "border-rose-300 text-rose-800 bg-rose-50"
                        }
                      >
                        {recheckResult.passed ? "Passed" : "Issues found"}
                      </Badge>
                      {recheckResult.canonical_brand_name && (
                        <span className="text-slate-600">
                          Brand: <strong>{recheckResult.canonical_brand_name}</strong>
                          {recheckResult.feed_brand_name &&
                            recheckResult.feed_brand_name !== recheckResult.canonical_brand_name && (
                              <span className="text-slate-400">
                                {" "}(feed: {recheckResult.feed_brand_name})
                              </span>
                            )}
                        </span>
                      )}
                      <span className="text-slate-500 tabular-nums">
                        {recheckResult.word_count?.toLocaleString()} words ·{" "}
                        {recheckResult.tracking_link_count ?? 0} tracking links
                      </span>
                    </div>
                    {(recheckResult.issues || []).length > 0 && (
                      <ul className="list-disc pl-4 space-y-1 text-rose-800">
                        {recheckResult.issues.map((item, i) => (
                          <li key={`issue-${i}`}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {(recheckResult.warnings || []).length > 0 && (
                      <ul className="list-disc pl-4 space-y-1 text-amber-800">
                        {recheckResult.warnings.map((item, i) => (
                          <li key={`warn-${i}`}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {recheckResult.fix_instructions_preview && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={applyRecheckFix}
                        className="border-indigo-300 text-indigo-800 hover:bg-indigo-50"
                        data-testid="aa-detail-recheck-fix-btn"
                      >
                        <MagicWand size={12} /> Fix with AI
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {(data.status === "published" || data.status === "image_error") && data.wp_post_id && (
              <div
                className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3"
                data-testid="aa-detail-ai-edit"
              >
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <MagicWand size={14} className="text-indigo-600" />
                  Update with AI
                </h3>
                <p className="text-[11px] text-slate-600">
                  Describe what to change. The live WordPress post is updated in place — slug and tracking links are preserved unless you ask otherwise.
                </p>
                <ArticleAiExtrasFields
                  referenceUrls={editUrls}
                  setReferenceUrls={setEditUrls}
                  instructions={editInstructions}
                  setInstructions={setEditInstructions}
                  instructionsRequired
                  idPrefix="aa-ai-edit"
                />
                <Button
                  onClick={handleAiEdit}
                  disabled={aiEditing || !editInstructions.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="aa-detail-ai-edit-btn"
                >
                  <MagicWand size={14} className={aiEditing ? "animate-pulse" : ""} />
                  {aiEditing ? "Updating…" : "Update with AI"}
                </Button>
              </div>
            )}

            {/* Retry history — every retry attempt (manual or queued)
                writes to retry_log so admins can see "this article
                has failed 4 times" at a glance. */}
            {history.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4" data-testid="aa-detail-retry-history">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <ArrowsClockwise size={14} className="text-slate-500" />
                  Retry history <span className="text-xs text-slate-500 font-normal">({history.length} attempts)</span>
                </h3>
                <ul className="space-y-2 text-xs">
                  {history.slice(0, 10).map((h, i) => (
                    <li key={i} className="flex items-start justify-between gap-3 py-1.5 border-b border-slate-200 last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            h.outcome === "published" ? "bg-emerald-100 text-emerald-700" :
                            h.outcome === "failed"    ? "bg-rose-100 text-rose-700" :
                                                         "bg-slate-100 text-slate-600"
                          }`}>
                            {h.outcome || "running"}
                          </span>
                          <span className="text-slate-600 font-mono text-[10px]">{h.trigger}</span>
                          <span className="text-slate-400 text-[10px]">{fmtDate(h.started_at)}</span>
                        </div>
                        {h.error_reason && (
                          <p className="text-rose-700 text-[10px] mt-0.5 truncate" title={h.error_reason}>
                            [{h.error_stage}] {h.error_reason}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function DetailField({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`text-slate-900 ${mono ? "font-mono text-[11px] break-all" : ""}`}>{value || "—"}</div>
    </div>
  );
}

// ─── Diagnostics tab ──────────────────────────────────────────────

function DiagnosticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bfNetwork, setBfNetwork] = useState("impact");
  const [bfDays, setBfDays] = useState(30);
  const [bfMaxPerPair, setBfMaxPerPair] = useState(1);
  const [bfResult, setBfResult] = useState(null);
  const [bfLoading, setBfLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get("/admin/auto-article/diagnostics");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load diagnostics");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runBackfill = async (dryRun) => {
    if (!dryRun && !window.confirm(`Publish backfill articles for ${bfNetwork || "all networks"} over last ${bfDays} days? Up to ${bfMaxPerPair} article(s) per (brand × pub) pair. This creates real WP posts.`)) return;
    setBfLoading(true);
    setBfResult(null);
    try {
      const r = await API.post(`/admin/auto-article/backfill`, null, {
        params: {
          network: bfNetwork || undefined,
          days: bfDays,
          dry_run: dryRun,
          max_articles_per_pair: bfMaxPerPair,
        },
        // Live backfill returns 202 immediately — worker runs async
        timeout: dryRun ? 60_000 : 30_000,
      });
      setBfResult(r.data);
      if (dryRun) {
        const articles = r.data.total_articles_to_create ?? r.data.candidates;
        toast.success(`Dry-run: ${r.data.candidates} pair(s) need ${articles} article(s) total`);
      } else {
        toast.success(
          `📥 Queued ${r.data.queued} article(s). Worker processes one at a time — watch Articles tab → In progress tile.`,
          { duration: 10000 },
        );
      }
    } catch (e) {
      const data = e?.response?.data;
      toast.error(data?.detail || "Backfill failed — check server logs");
    }
    finally { setBfLoading(false); }
  };

  if (loading && !data) {
    return <p className="text-sm text-slate-500" data-testid="aa-diag-loading">Loading diagnostics…</p>;
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="aa-diag-panel">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="aa-diag-refresh-btn">
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />Refresh
        </Button>
      </div>

      {/* Env */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
          <CheckCircle weight="duotone" size={16} className="text-indigo-600" /> Environment variables
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          {Object.entries(data.env).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-3 py-2 rounded border border-slate-100 bg-slate-50">
              <span className="font-mono text-slate-700">{k}</span>
              {v.set ? (
                <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                  <CheckCircle size={12} weight="fill" /> {v.preview || "set"}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-rose-600 font-medium">
                  <Warning size={12} weight="fill" /> NOT SET
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* System */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
          <Pulse weight="duotone" size={16} className="text-indigo-600" /> System dependencies
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {["playwright_module", "beautifulsoup", "colorthief", "chromium_installed"].map((key) => (
            <div key={key} className={`px-3 py-2 rounded border ${data.system[key] ? "border-emerald-200 bg-emerald-50/50 text-emerald-800" : "border-rose-200 bg-rose-50/50 text-rose-700"}`}>
              <div className="font-mono">{key.replace(/_/g, " ")}</div>
              <div className="font-semibold mt-0.5">{data.system[key] ? "OK" : "MISSING"}</div>
            </div>
          ))}
        </div>
        {!data.system.chromium_installed && (
          <p className="text-xs text-rose-700 mt-3 bg-rose-50 px-3 py-2 rounded border border-rose-100" data-testid="aa-chromium-warn">
            Chromium binary not detected. Run on the server:{" "}
            <code className="bg-white px-1 py-0.5 rounded">playwright install chromium</code>
          </p>
        )}
        {(data.system.playwright_error || data.system.beautifulsoup_error) && (
          <pre className="text-xs text-rose-700 mt-3 bg-rose-50 px-3 py-2 rounded border border-rose-100 overflow-auto">
            {data.system.playwright_error || data.system.beautifulsoup_error}
          </pre>
        )}
      </div>

      {/* Per-network funnel */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
            <Storefront weight="duotone" size={16} className="text-indigo-600" />
            Per-network funnel <span className="font-normal text-slate-500">(last {data.lookback_days}d, ≥ {fmtUsd(data.sale_threshold_usd)})</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            <strong>Pipeline-ready</strong> = conversions that pass every join (network → brand → publisher).
            A big drop from <em>qualifying</em> to <em>brand-resolved</em> means the brand catalog is missing entries.
            A drop from <em>brand-resolved</em> to <em>publisher-resolved</em> means publishers aren&apos;t seeded with those sub_ids.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="aa-diag-networks">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Network</th>
                <th className="text-right px-4 py-2 font-medium">Qualifying</th>
                <th className="text-right px-4 py-2 font-medium">Brand-resolved</th>
                <th className="text-right px-4 py-2 font-medium">Publisher-resolved</th>
                <th className="text-right px-4 py-2 font-medium">Pipeline-ready</th>
                <th className="text-left px-4 py-2 font-medium">Health</th>
              </tr>
            </thead>
            <tbody>
              {data.networks.map((n) => {
                const q = n.qualifying_90d;
                const pr = n.pipeline_ready;
                let health = "ok"; let label = "Healthy";
                if (q === 0) { health = "neutral"; label = "No data"; }
                else if (pr === 0) { health = "fail"; label = "0 published — investigate"; }
                else if (pr < q * 0.3) { health = "warn"; label = "Partial coverage"; }
                return (
                  <tr key={n.network} className="border-t border-slate-100" data-testid={`aa-diag-net-${n.network}`}>
                    <td className="px-4 py-2 font-mono text-slate-700">{n.network}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{q}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{n.brand_resolved}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{n.publisher_resolved}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">{pr}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={
                        health === "ok" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : health === "warn" ? "border-amber-200 bg-amber-50 text-amber-700"
                        : health === "fail" ? "border-rose-200 bg-rose-50 text-rose-700"
                        : "border-slate-200 bg-slate-50 text-slate-500"
                      }>{label}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cleanup duplicates panel */}
      <CleanupDuplicatesPanel />

      {/* FTC disclosure backfill */}
      <DisclosureBackfillPanel />

      {/* Backfill panel */}
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4" data-testid="aa-backfill-panel">
        <h2 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
          <MagicWand weight="duotone" size={16} className="text-indigo-600" /> One-shot backfill
        </h2>
        <p className="text-xs text-slate-600 mb-3">
          Tier-aware catch-up. Aggregates conversions by <strong>(brand × publisher)</strong> over the window, applies the tier quota (entry tier <strong>$10 – $299 = 1 article</strong>; +1 article per $5k beyond $5k cumulative gross), and publishes <em>up to N</em> per pair in this run. Caps at <strong>{bfMaxPerPair}</strong> per pair so a $300k brand doesn&apos;t dump 60+ articles in one click — the hourly polling job fills the rest naturally.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-700">
            Network
            <select value={bfNetwork} onChange={(e) => setBfNetwork(e.target.value)} className="block mt-1 px-2 py-1.5 border border-slate-200 rounded text-sm bg-white" data-testid="aa-backfill-network">
              <option value="">All networks</option>
              {data.networks.map((n) => <option key={n.network} value={n.network}>{n.network}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-700">
            Days back
            <Input type="number" min="1" max="365" value={bfDays} onChange={(e) => setBfDays(Number(e.target.value) || 30)} className="block mt-1 w-24" data-testid="aa-backfill-days" />
          </label>
          <label className="text-xs text-slate-700">
            Max articles / pair
            <Input type="number" min="1" max="20" value={bfMaxPerPair} onChange={(e) => setBfMaxPerPair(Number(e.target.value) || 1)} className="block mt-1 w-24" data-testid="aa-backfill-max-per-pair" />
          </label>
          <Button size="sm" variant="outline" onClick={() => runBackfill(true)} disabled={bfLoading} data-testid="aa-backfill-dryrun-btn">
            <Eye size={14} className={bfLoading ? "animate-spin" : ""} />Dry-run
          </Button>
          <Button size="sm" onClick={() => runBackfill(false)} disabled={bfLoading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="aa-backfill-live-btn">
            <MagicWand size={14} className={bfLoading ? "animate-spin" : ""} />Publish now
          </Button>
        </div>

        {bfResult && (
          <div className="mt-4 p-3 bg-white rounded border border-slate-200 text-xs" data-testid="aa-backfill-result">
            <div className="flex flex-wrap gap-4 mb-2">
              <span><strong>Mode:</strong> {bfResult.mode}</span>
              <span><strong>Network:</strong> {bfResult.network || "all"}</span>
              <span><strong>Days:</strong> {bfResult.days}</span>
              {bfResult.mode === "dry-run" ? (
                <>
                  <span><strong>Qualifying pairs:</strong> {bfResult.total_qualifying_pairs ?? bfResult.total_qualifying}</span>
                  <span><strong>Pairs needing articles:</strong> {bfResult.candidates}</span>
                  <span><strong>Total articles to create:</strong> {bfResult.total_articles_to_create ?? bfResult.candidates}</span>
                  <span><strong>Already published:</strong> {bfResult.already_processed}</span>
                </>
              ) : bfResult.mode === "queued" ? (
                <>
                  <span><strong>Pairs queued:</strong> {bfResult.checked_pairs}</span>
                  <span className="text-indigo-700"><strong>Articles in queue:</strong> {bfResult.queued}</span>
                  <span className="text-xs text-slate-500 italic">{bfResult.message}</span>
                </>
              ) : (
                <>
                  <span><strong>Pairs checked:</strong> {bfResult.checked_pairs ?? bfResult.checked}</span>
                  <span className="text-emerald-700"><strong>Published:</strong> {bfResult.published}</span>
                  <span className="text-amber-700"><strong>Skipped:</strong> {bfResult.skipped}</span>
                </>
              )}
            </div>
            {bfResult.preview?.length > 0 && (
              <details open>
                <summary className="cursor-pointer font-medium text-slate-700">{bfResult.preview.length} pair preview</summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead className="text-slate-500 bg-slate-50">
                      <tr>
                        <th className="text-left px-2 py-1">Brand</th>
                        <th className="text-left px-2 py-1">Publisher</th>
                        <th className="text-right px-2 py-1">Cumulative</th>
                        <th className="text-center px-2 py-1">Tier quota</th>
                        <th className="text-center px-2 py-1">Have</th>
                        <th className="text-center px-2 py-1 text-indigo-700">Create now</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bfResult.preview.map((p) => (
                        <tr key={p.conversion_id} className="border-t border-slate-100">
                          <td className="px-2 py-1 font-medium text-slate-800">{p.brand_name}</td>
                          <td className="px-2 py-1 text-slate-600">{p.publisher_email || "—"}</td>
                          <td className="px-2 py-1 text-right tabular-nums text-emerald-700">{fmtUsd(p.cumulative_gross_usd ?? p.gross_sale)}</td>
                          <td className="px-2 py-1 text-center tabular-nums">{p.target_articles ?? "—"}</td>
                          <td className="px-2 py-1 text-center tabular-nums">{p.existing_articles ?? 0}</td>
                          <td className="px-2 py-1 text-center tabular-nums font-semibold text-indigo-700">{p.articles_to_create ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}




function DisclosureBackfillPanel() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [missing, setMissing] = useState({ rows: [], total: 0, total_pages: 0 });
  const [missingLoading, setMissingLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const [logData, setLogData] = useState({ rows: [], total: 0, total_pages: 0 });
  const [logLoading, setLogLoading] = useState(false);
  const [autoStatus, setAutoStatus] = useState(null);

  const loadAutoStatus = useCallback(async () => {
    try {
      const r = await API.get("/admin/auto-article/articles/disclosure-auto-status");
      setAutoStatus(r.data);
    } catch {
      setAutoStatus(null);
    }
  }, []);

  const loadMissing = useCallback(async (p = page, ps = pageSize) => {
    setMissingLoading(true);
    try {
      const r = await API.get("/admin/auto-article/articles/missing-disclosure", {
        params: { page: p, page_size: ps },
      });
      setMissing(r.data);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load missing-disclosure list");
    } finally {
      setMissingLoading(false);
    }
  }, [page, pageSize]);

  const loadLog = useCallback(async (p = logPage) => {
    setLogLoading(true);
    try {
      const r = await API.get("/admin/auto-article/articles/disclosure-log", {
        params: { page: p, page_size: 30 },
      });
      setLogData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load disclosure log");
    } finally {
      setLogLoading(false);
    }
  }, [logPage]);

  useEffect(() => { loadMissing(page, pageSize); }, [page, pageSize, loadMissing]);
  useEffect(() => { loadLog(logPage); }, [logPage, loadLog]);
  useEffect(() => {
    loadAutoStatus();
    const t = setInterval(loadAutoStatus, 30_000);
    return () => clearInterval(t);
  }, [loadAutoStatus]);

  const toggleOne = (cid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    const ids = (missing.rows || []).map((r) => r.conversion_id);
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(ids));
    }
  };

  const runBatch = async (dryRun, conversionIds) => {
    if (!conversionIds?.length) {
      toast.error("No articles selected");
      return;
    }
    if (!dryRun && !window.confirm(
      `Add disclosure to ${conversionIds.length} article(s)? (~3s between each WP request to avoid 503s).`,
    )) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await API.post("/admin/auto-article/articles/bulk-fix-disclosure", {
        conversion_ids: conversionIds,
        dry_run: dryRun,
        inter_row_sleep_s: 3.0,
      }, { timeout: 600_000 });
      setResult(r.data);
      if (dryRun) {
        toast.success(
          `Dry-run: ${r.data.patched} need disclosure, ${r.data.already_has} already OK (${r.data.scanned} scanned)`,
        );
      } else {
        toast.success(
          `Added ${r.data.patched}; ${r.data.already_has} already had it; ${r.data.errors} error(s)`,
        );
        loadMissing(page, pageSize);
        loadLog(1);
        loadAutoStatus();
        setLogPage(1);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disclosure backfill failed");
    } finally {
      setLoading(false);
    }
  };

  const runFirstN = (n, dryRun) => {
    const ids = (missing.rows || []).slice(0, n).map((r) => r.conversion_id);
    runBatch(dryRun, ids);
  };

  const pageStart = missing.total === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(page * pageSize, missing.total);
  const needsPatch = (result?.per_post || []).filter(
    (p) => p.status === "would_patch" || p.status === "patched",
  );
  const errors = (result?.per_post || []).filter((p) => p.error || p.skipped);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 space-y-4" data-testid="aa-disclosure-backfill-panel">
      <div>
        <h2 className="font-semibold text-slate-900 text-sm mb-2 flex items-center gap-2">
          <Warning weight="duotone" size={16} className="text-amber-600" /> FTC disclosure backfill
        </h2>
        <p className="text-xs text-slate-600">
          Published articles that still need our styled disclosure block appear below.
          <strong className="text-amber-800"> Dry-run only previews</strong> (log shows <code className="text-[10px]">would_add (dry)</code> — WordPress is not updated).
          Use <strong>Add to first 10/20</strong> to actually insert disclosure.
          Background worker adds <strong>1 article every 2 min</strong> automatically when enabled.
        </p>
        {autoStatus && (
          <div className="mt-2 text-[11px] flex flex-wrap gap-3 items-center" data-testid="aa-disclosure-auto-status">
            <Badge variant="outline" className={autoStatus.enabled ? "border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500"}>
              Auto {autoStatus.enabled ? "ON" : "OFF"}
            </Badge>
            <span className="text-slate-600">Every {autoStatus.interval_s}s</span>
            <span className="text-slate-600">{autoStatus.pending ?? "—"} pending</span>
            {autoStatus.last_run_at && (
              <span className="text-slate-500">Last run {fmtDateShort(autoStatus.last_run_at)}</span>
            )}
            {autoStatus.last_result?.patched > 0 && (
              <span className="text-emerald-700">Last: added 1</span>
            )}
            {autoStatus.last_error && (
              <span className="text-rose-700">Last error: {autoStatus.last_error.slice(0, 80)}</span>
            )}
          </div>
        )}
      </div>

      {/* Missing disclosure queue */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-semibold text-slate-700">
            Missing disclosure
            <span className="ml-2 font-normal text-slate-500">({missing.total} total)</span>
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[11px] text-slate-600 flex items-center gap-1">
              Per page
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                data-testid="aa-disclosure-page-size"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </label>
            <Button variant="outline" size="sm" onClick={() => loadMissing(page, pageSize)} disabled={missingLoading} className="h-7 text-xs">
              <ArrowsClockwise size={12} className={missingLoading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 sticky top-0">
              <tr>
                <th className="px-2 py-1.5 w-8">
                  <input
                    type="checkbox"
                    checked={
                      (missing.rows || []).length > 0
                      && (missing.rows || []).every((r) => selected.has(r.conversion_id))
                    }
                    onChange={toggleAllOnPage}
                    data-testid="aa-disclosure-select-all"
                  />
                </th>
                <th className="text-left px-2 py-1.5">Brand</th>
                <th className="text-left px-2 py-1.5">Title</th>
                <th className="text-left px-2 py-1.5">Type</th>
                <th className="text-left px-2 py-1.5">Published</th>
                <th className="text-left px-2 py-1.5">WP</th>
              </tr>
            </thead>
            <tbody>
              {missingLoading && !(missing.rows || []).length ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
              ) : !(missing.rows || []).length ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">All articles have the current disclosure format logged.</td></tr>
              ) : (missing.rows || []).map((r) => (
                <tr key={r.conversion_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={selected.has(r.conversion_id)}
                      onChange={() => toggleOne(r.conversion_id)}
                      data-testid={`aa-disclosure-check-${r.conversion_id}`}
                    />
                  </td>
                  <td className="px-2 py-1.5 font-medium text-slate-800">{r.brand_name || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-600 max-w-[200px] truncate" title={r.article_title}>{r.article_title || "—"}</td>
                  <td className="px-2 py-1.5">
                    <Badge variant="outline" className={r.expected_disclosure_type === "amazon" ? "border-orange-200 text-orange-700 text-[10px]" : "border-slate-200 text-slate-600 text-[10px]"}>
                      {r.expected_disclosure_type || "generic"}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{fmtDateShort(r.published_at)}</td>
                  <td className="px-2 py-1.5">
                    {r.wp_post_url ? (
                      <a href={r.wp_post_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-0.5">
                        #{r.wp_post_id} <ArrowSquareOut size={10} />
                      </a>
                    ) : (
                      <code className="text-slate-400">#{r.wp_post_id}</code>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-3 py-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11px] text-slate-500">
            {missing.total ? `${pageStart}–${pageEnd} of ${missing.total}` : "0 rows"}
            {selected.size > 0 && <span className="ml-2 text-amber-700">{selected.size} selected</span>}
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            <Button variant="outline" size="sm" disabled={page <= 1 || missingLoading} onClick={() => setPage((p) => p - 1)} className="h-7 text-xs">Prev</Button>
            <span className="text-[11px] text-slate-500 px-1">p.{page}/{missing.total_pages || 1}</span>
            <Button variant="outline" size="sm" disabled={page >= (missing.total_pages || 1) || missingLoading} onClick={() => setPage((p) => p + 1)} className="h-7 text-xs">Next</Button>
          </div>
        </div>
      </div>

      {/* Batch actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => runFirstN(10, true)} disabled={loading || !(missing.rows || []).length} className="h-8 text-xs" data-testid="aa-disclosure-dryrun-10">
          <Eye size={12} /> Dry-run first 10
        </Button>
        <Button size="sm" variant="outline" onClick={() => runFirstN(20, true)} disabled={loading || !(missing.rows || []).length} className="h-8 text-xs" data-testid="aa-disclosure-dryrun-20">
          <Eye size={12} /> Dry-run first 20
        </Button>
        <Button size="sm" onClick={() => runFirstN(10, false)} disabled={loading || !(missing.rows || []).length} className="h-8 text-xs bg-amber-600 hover:bg-amber-700" data-testid="aa-disclosure-add-10">
          <Warning size={12} /> Add to first 10
        </Button>
        <Button size="sm" onClick={() => runFirstN(20, false)} disabled={loading || !(missing.rows || []).length} className="h-8 text-xs bg-amber-600 hover:bg-amber-700" data-testid="aa-disclosure-add-20">
          <Warning size={12} /> Add to first 20
        </Button>
        <Button size="sm" onClick={() => runBatch(false, [...selected])} disabled={loading || selected.size === 0} className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700" data-testid="aa-disclosure-add-selected">
          Add selected ({selected.size})
        </Button>
      </div>

      {result && (
        <div className="p-3 bg-white rounded border border-slate-200 text-xs" data-testid="aa-disclosure-backfill-result">
          <div className="flex flex-wrap gap-4 mb-2">
            <span><strong>Mode:</strong> {result.dry_run ? "dry-run" : "live"}</span>
            <span><strong>Scanned:</strong> {result.scanned}</span>
            <span className="text-amber-700"><strong>{result.dry_run ? "Would add:" : "Added:"}</strong> {result.patched}</span>
            <span className="text-emerald-700"><strong>Already has:</strong> {result.already_has}</span>
            {result.errors > 0 && <span className="text-rose-700"><strong>Errors:</strong> {result.errors}</span>}
          </div>
          {needsPatch.length > 0 && (
            <details open>
              <summary className="cursor-pointer font-medium text-slate-700">{needsPatch.length} detail row(s)</summary>
              <ul className="mt-2 space-y-1 text-[11px] max-h-32 overflow-y-auto">
                {needsPatch.map((p) => (
                  <li key={p.wp_post_id || p.conversion_id} className="border-b border-slate-100 py-1">
                    <strong>{p.brand_name || "—"}</strong> · wp#{p.wp_post_id} · {p.disclosure_type || "—"}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {errors.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer font-medium text-rose-700">{errors.length} error(s)</summary>
              <ul className="mt-2 space-y-1 text-[11px]">
                {errors.map((p, i) => (
                  <li key={i} className="text-rose-700">wp#{p.wp_post_id || "?"} — {p.error || p.skipped}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Audit log */}
      <details className="rounded-lg border border-slate-200 bg-white" open={logData.total > 0}>
        <summary className="px-3 py-2 cursor-pointer text-xs font-semibold text-slate-700">
          Disclosure log ({logData.total} events)
        </summary>
        <div className="border-t border-slate-100 max-h-48 overflow-y-auto">
          {logLoading ? (
            <p className="px-3 py-4 text-xs text-slate-500">Loading log…</p>
          ) : !(logData.rows || []).length ? (
            <p className="px-3 py-4 text-xs text-slate-500">No disclosure events yet.</p>
          ) : (
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1">When</th>
                  <th className="text-left px-2 py-1">Action</th>
                  <th className="text-left px-2 py-1">Brand</th>
                  <th className="text-left px-2 py-1">Type</th>
                  <th className="text-left px-2 py-1">WP</th>
                </tr>
              </thead>
              <tbody>
                {(logData.rows || []).map((e, i) => (
                  <tr key={`${e.at}-${e.conversion_id}-${i}`} className="border-t border-slate-100">
                    <td className="px-2 py-1 text-slate-500 whitespace-nowrap">{fmtDateShort(e.at)}</td>
                    <td className="px-2 py-1">
                      <Badge variant="outline" className={
                        e.action === "added" ? "border-emerald-200 text-emerald-700 text-[10px]"
                        : e.action === "error" ? "border-rose-200 text-rose-700 text-[10px]"
                        : e.action === "would_add" ? "border-amber-200 text-amber-700 text-[10px]"
                        : e.action === "already_present" ? "border-slate-200 text-slate-600 text-[10px]"
                        : "border-slate-200 text-slate-600 text-[10px]"
                      }>
                        {e.action === "would_add" ? "preview only (dry)" : e.action}
                        {e.action !== "would_add" && e.dry_run ? " (dry)" : ""}
                      </Badge>
                    </td>
                    <td className="px-2 py-1 text-slate-800">{e.brand_name || "—"}</td>
                    <td className="px-2 py-1 text-slate-500">{e.disclosure_type || "—"}</td>
                    <td className="px-2 py-1">
                      {e.wp_post_url ? (
                        <a href={e.wp_post_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">#{e.wp_post_id}</a>
                      ) : (
                        <span className="text-slate-400">#{e.wp_post_id || "—"}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {logData.total_pages > 1 && (
          <div className="px-3 py-2 border-t border-slate-100 flex justify-end gap-2">
            <Button variant="outline" size="sm" disabled={logPage <= 1} onClick={() => setLogPage((p) => p - 1)} className="h-7 text-xs">Older</Button>
            <span className="text-[11px] text-slate-500 self-center">p.{logPage}/{logData.total_pages}</span>
            <Button variant="outline" size="sm" disabled={logPage >= logData.total_pages} onClick={() => setLogPage((p) => p + 1)} className="h-7 text-xs">Newer</Button>
          </div>
        )}
      </details>
    </div>
  );
}




function CleanupDuplicatesPanel() {
  const [keepN, setKeepN] = useState(1);
  const [deleteFromWp, setDeleteFromWp] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (dryRun) => {
    if (!dryRun && !window.confirm(`This will DELETE duplicate posts ${deleteFromWp ? "from BOTH WordPress AND the article_log" : "from article_log only"}, keeping the newest ${keepN} per (brand × pub). Continue?`)) return;
    setLoading(true); setResult(null);
    try {
      const r = await API.post("/admin/auto-article/cleanup-duplicates", null, {
        params: { keep_newest: keepN, delete_from_wp: deleteFromWp, dry_run: dryRun },
      });
      setResult(r.data);
      if (dryRun) {
        toast.success(`Dry-run: ${r.data.total_to_delete} duplicate(s) across ${r.data.pairs_with_duplicates} pair(s)`);
      } else {
        toast.success(`Deleted ${r.data.wp_posts_deleted} WP posts + ${r.data.article_log_rows_deleted} log rows`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Cleanup failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-4" data-testid="aa-cleanup-panel">
      <h2 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
        <X weight="bold" size={16} className="text-rose-600" /> Cleanup duplicate articles
      </h2>
      <p className="text-xs text-slate-600 mb-3">
        Removes burst-duplicates from the pre-v12 era (when each $300+ conversion became its own article). Keeps the newest <strong>{keepN}</strong> per (brand × pub) pair and deletes the rest from {deleteFromWp ? "both WordPress AND article_log" : "article_log only"}. Always start with Dry-run.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-700">
          Keep newest per pair
          <Input type="number" min="1" max="10" value={keepN} onChange={(e) => setKeepN(Number(e.target.value) || 1)} className="block mt-1 w-24" data-testid="aa-cleanup-keep" />
        </label>
        <label className="text-xs text-slate-700 flex items-center gap-1.5 pb-2">
          <input type="checkbox" checked={deleteFromWp} onChange={(e) => setDeleteFromWp(e.target.checked)} data-testid="aa-cleanup-wp" />
          Also delete from WordPress
        </label>
        <Button size="sm" variant="outline" onClick={() => run(true)} disabled={loading} data-testid="aa-cleanup-dryrun-btn">
          <Eye size={14} className={loading ? "animate-spin" : ""} />Dry-run
        </Button>
        <Button size="sm" onClick={() => run(false)} disabled={loading} className="bg-rose-600 hover:bg-rose-700" data-testid="aa-cleanup-live-btn">
          <X size={14} weight="bold" className={loading ? "animate-spin" : ""} />Delete duplicates
        </Button>
      </div>

      {result && (
        <div className="mt-4 p-3 bg-white rounded border border-slate-200 text-xs" data-testid="aa-cleanup-result">
          <div className="flex flex-wrap gap-4 mb-2">
            <span><strong>Mode:</strong> {result.mode}</span>
            <span><strong>Pairs scanned:</strong> {result.scanned_pairs}</span>
            {result.mode === "dry-run" ? (
              <>
                <span><strong>Pairs with dupes:</strong> {result.pairs_with_duplicates}</span>
                <span className="text-rose-700"><strong>To delete:</strong> {result.total_to_delete}</span>
              </>
            ) : (
              <>
                <span className="text-rose-700"><strong>WP deleted:</strong> {result.wp_posts_deleted}</span>
                <span className="text-rose-700"><strong>Log deleted:</strong> {result.article_log_rows_deleted}</span>
                {result.errors?.length > 0 && <span className="text-amber-700"><strong>Errors:</strong> {result.errors.length}</span>}
              </>
            )}
          </div>
          {result.preview?.length > 0 && (
            <details open>
              <summary className="cursor-pointer font-medium text-slate-700">{result.preview.length} pairs with duplicates</summary>
              <ul className="mt-2 space-y-1 text-[11px]">
                {result.preview.map((p, i) => (
                  <li key={i} className="border-b border-slate-100 py-1.5">
                    <strong className="text-slate-800">{p.brand_name}</strong> × <code className="text-slate-500">{p.publisher_id.slice(0, 12)}…</code> — total {p.total}, keep {p.keep}, <span className="text-rose-700 font-semibold">delete {p.delete}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {result.errors?.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-rose-700 font-medium">{result.errors.length} errors</summary>
              <pre className="mt-2 max-h-48 overflow-auto bg-rose-50 p-2 rounded border border-rose-200 font-mono text-[10px]">{result.errors.join("\n")}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
